import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Clock,
  User,
  X,
  Grid3X3,
  List,
  Layers,
  Search,
  RefreshCw
} from 'lucide-react';
import { SchedulingAssistant } from './SchedulingAssistant';
import { AppointmentEditModal } from './AppointmentEditModal';

// ============================================================================
// TYPES
// ============================================================================

type AttendanceType = 'required' | 'optional';

interface Participant {
  id: string;
  type: 'staff' | 'client';
  name: string;
  status?: 'pending' | 'confirmed' | 'declined' | 'tentative';
  attendance?: AttendanceType;
}

type EventType = 'appointment' | 'meeting' | 'personal';
type FreeBusyStatus = 'busy' | 'out_of_office' | 'tentative' | 'free';

interface Appointment {
  id: string;
  organizer: Participant;
  attendees: Participant[];
  title: string;
  notes?: string;
  start_time: string;
  end_time: string;
  duration_minutes: number;
  status: 'scheduled' | 'completed' | 'cancelled' | 'no-show' | 'pending_approval';
  event_type: EventType;
  free_busy_status?: FreeBusyStatus;
  all_day?: boolean;
  color?: string | null;
  created_at: string;
  updated_at: string;
  series_id?: string | null;
  instance_index?: number | null;
  is_exception?: boolean;
  google_event_id?: string | null;
  booking?: {
    service_id?: string;
    requested_by_name?: string;
    requested_by_email?: string;
    requested_by_phone?: string;
    approval_status?: string;
    session_method?: string;
  };
}

type ViewMode = 'month' | 'week' | 'day';

interface CoachCalendarViewProps {
  onBack?: () => void;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const WEEKDAYS_ABBR = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const WEEKDAYS_SHORT = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS_FULL = ['January', 'February', 'March', 'April', 'May', 'June',
                     'July', 'August', 'September', 'October', 'November', 'December'];

// Generate time slots for week/day view (full 24 hours)
const START_HOUR = 0;
const TOTAL_HOURS = 24;
const PX_PER_HOUR = 80; // pixels per hour in week/day grid

// Generate 30-minute time slots for the grid
const TIME_SLOTS = Array.from({ length: TOTAL_HOURS * 2 }, (_, i) => {
  const hour = START_HOUR + Math.floor(i / 2);
  const isHalfHour = i % 2 === 1;
  const minutes = isHalfHour ? 30 : 0;
  let label: string;
  if (hour === 0 && !isHalfHour) label = '12 AM';
  else if (hour === 12 && !isHalfHour) label = '12 PM';
  else if (!isHalfHour) label = hour > 12 ? `${hour - 12} PM` : `${hour} AM`;
  else label = hour === 0 ? '12:30' : hour === 12 ? '12:30' : hour > 12 ? `${hour - 12}:30` : `${hour}:30`;

  return { hour, minutes, isHalfHour, label, fractionalHour: hour + minutes / 60 };
});

// Build tooltip string with full appointment details
const getAptTooltip = (apt: Appointment, serviceNames: Record<string, string>): string => {
  const typeLabels: Record<string, string> = { appointment: 'Appointment', meeting: 'Meeting', personal: 'Personal' };
  const lines = [apt.title];
  lines.push(`Type: ${typeLabels[apt.event_type] || 'Appointment'}`);

  if (apt.event_type === 'personal') {
    const statusLabels: Record<string, string> = { busy: 'Busy', out_of_office: 'Out of Office', tentative: 'Tentative', free: 'Free' };
    lines.push(`Show as: ${statusLabels[apt.free_busy_status || 'busy']}`);
  } else {
    const client = getClientName(apt);
    if (client !== 'Unknown Client') lines.push(`Client: ${client}`);
    const serviceName = apt.booking?.service_id ? serviceNames[apt.booking.service_id] : null;
    if (serviceName) lines.push(`Service: ${serviceName}`);
    if (apt.booking?.session_method) lines.push(`Method: ${apt.booking.session_method}`);
  }

  if (apt.event_type === 'meeting' && apt.attendees.length > 0) {
    const attendeeNames = apt.attendees.map(a => {
      const statusIcon = a.status === 'confirmed' ? '✓' : a.status === 'declined' ? '✕' : a.status === 'tentative' ? '?' : '○';
      return `${statusIcon} ${a.name || a.id}`;
    });
    lines.push(`Attendees:\n  ${attendeeNames.join('\n  ')}`);
  }

  lines.push(`Duration: ${apt.duration_minutes} min`);
  if (apt.status !== 'scheduled') lines.push(`Status: ${apt.status.replace('_', ' ')}`);
  if (apt.booking?.requested_by_email) lines.push(`Email: ${apt.booking.requested_by_email}`);
  if (apt.booking?.requested_by_phone) lines.push(`Phone: ${apt.booking.requested_by_phone}`);
  if (apt.notes) lines.push(`Notes: ${apt.notes}`);
  return lines.join('\n');
};

// Default colors by event type
const EVENT_TYPE_COLORS: Record<string, string> = {
  appointment: '#10b981', // green
  meeting: '#3b82f6',     // blue
  personal: '#3b82f6',    // blue (default — overridden by free/busy status)
};

// Resolve appointment color: custom > service > event type default
const getAptColor = (apt: Appointment, serviceColors: Record<string, string>): string => {
  // 1. Custom color on the event itself
  if (apt.color) return apt.color;
  // 2. Service color (for appointments with a booking)
  const serviceId = apt.booking?.service_id;
  if (serviceId && serviceColors[serviceId]) return serviceColors[serviceId];
  // 3. Event type default
  return EVENT_TYPE_COLORS[apt.event_type] || '#10b981';
};

// Resolve the viewer's effective display status for an appointment
// This drives the left border style (solid/striped/outlined/hidden)
const getViewerStatus = (apt: Appointment, currentUserId: string): 'busy' | 'tentative' | 'free' | 'declined' | 'out_of_office' => {
  // Personal events use their own free_busy_status
  if (apt.event_type === 'personal') return apt.free_busy_status || 'busy';

  // For meetings, check the viewer's attendee response
  if (apt.event_type === 'meeting') {
    if (apt.organizer?.id === currentUserId) return 'busy'; // Organizer is always committed
    const myRecord = (apt.attendees || []).find(a => a.id === currentUserId);
    if (!myRecord) return 'busy'; // Shouldn't happen, but default to busy
    if (myRecord.status === 'confirmed') return 'busy';
    if (myRecord.status === 'tentative') return 'tentative';
    if (myRecord.status === 'declined') return 'declined';
    return 'tentative'; // 'pending' treated as tentative
  }

  // Client appointments — always busy
  return 'busy';
};

// Resolve the left border accent color based on viewer status
const getAptBorderColor = (apt: Appointment, baseColor: string, currentUserId: string): string => {
  const status = getViewerStatus(apt, currentUserId);
  // OOO always gets purple border regardless of block color
  if (status === 'out_of_office') return '#7c3aed';
  // Everything else uses a darker shade of the block color
  return `color-mix(in srgb, ${baseColor} 70%, black)`;
};

// Get CSS classes for viewer-specific border rendering
const getViewerBorderClasses = (apt: Appointment, currentUserId: string): string => {
  const status = getViewerStatus(apt, currentUserId);
  if (status === 'tentative') return 'tentative-stripes';
  if (status === 'free') return 'free-status';
  if (status === 'declined') return 'declined-status';
  return '';
};

// Get the subtitle line for an appointment (client name, attendees, or status label)
const getAptSubtitle = (apt: Appointment): string => {
  if (apt.event_type === 'personal') {
    const labels: Record<string, string> = { busy: 'Busy', out_of_office: 'Out of Office', tentative: 'Tentative', free: 'Free' };
    const defaultLabel = labels[apt.free_busy_status || 'busy'] || 'Busy';
    // If the title is custom (not just one of the show-as defaults), prefer it.
    // Externally-synced events have meaningful titles from the source calendar.
    if (apt.title && !Object.values(labels).includes(apt.title)) {
      return apt.title;
    }
    return defaultLabel;
  }
  if (apt.event_type === 'meeting') {
    const staffCount = apt.attendees.filter(a => a.type === 'staff').length;
    if (staffCount > 0) return `${staffCount + 1} attendee${staffCount > 0 ? 's' : ''}`;
    return 'Meeting';
  }
  return getClientName(apt);
};

// Get client name from appointment attendees
const getClientName = (apt: Appointment): string => {
  const client = apt.attendees.find(a => a.type === 'client');
  return client?.name || apt.booking?.requested_by_name || 'Unknown Client';
};

// Get client ID from appointment attendees
const getClientId = (apt: Appointment): string => {
  const client = apt.attendees.find(a => a.type === 'client');
  return client?.id || '';
};

// ============================================================================
// MINI CALENDAR COMPONENT (Sidebar)
// ============================================================================

interface MiniCalendarSidebarProps {
  currentDate: Date;
  selectedDate: Date;
  viewMode: ViewMode;
  onDateClick: (date: Date) => void;
  onDateDoubleClick: (date: Date) => void;
  onMonthChange: (date: Date) => void;
}

const MiniCalendarSidebar: React.FC<MiniCalendarSidebarProps> = ({
  currentDate,
  selectedDate,
  viewMode,
  onDateClick,
  onDateDoubleClick,
  onMonthChange: _onMonthChange
}) => {
  const [displayMonth, setDisplayMonth] = useState(currentDate.getMonth());
  const [displayYear, setDisplayYear] = useState(currentDate.getFullYear());
  const [clickTimeout, setClickTimeout] = useState<NodeJS.Timeout | null>(null);

  // Sync display month when currentDate changes externally
  useEffect(() => {
    setDisplayMonth(currentDate.getMonth());
    setDisplayYear(currentDate.getFullYear());
  }, [currentDate]);

  const goToPreviousMonth = () => {
    if (displayMonth === 0) {
      setDisplayMonth(11);
      setDisplayYear(displayYear - 1);
    } else {
      setDisplayMonth(displayMonth - 1);
    }
  };

  const goToNextMonth = () => {
    if (displayMonth === 11) {
      setDisplayMonth(0);
      setDisplayYear(displayYear + 1);
    } else {
      setDisplayMonth(displayMonth + 1);
    }
  };

  const goToToday = () => {
    const today = new Date();
    setDisplayMonth(today.getMonth());
    setDisplayYear(today.getFullYear());
    onDateClick(today);
  };

  // Generate calendar days
  const generateDays = () => {
    const firstDay = new Date(displayYear, displayMonth, 1);
    const firstCalendarDay = new Date(firstDay);
    firstCalendarDay.setDate(firstDay.getDate() - firstDay.getDay());

    const days: { date: Date; isCurrentMonth: boolean; isToday: boolean; isSelected: boolean }[] = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    for (let i = 0; i < 42; i++) {
      const date = new Date(firstCalendarDay);
      date.setDate(firstCalendarDay.getDate() + i);

      const isToday = date.toDateString() === today.toDateString();
      const isSelected = date.toDateString() === selectedDate.toDateString();
      const isCurrentMonth = date.getMonth() === displayMonth;

      days.push({ date, isCurrentMonth, isToday, isSelected });
    }

    return days;
  };

  // Handle click with double-click detection
  const handleClick = (date: Date) => {
    if (clickTimeout) {
      clearTimeout(clickTimeout);
      setClickTimeout(null);
      onDateDoubleClick(date);
    } else {
      const timeout = setTimeout(() => {
        onDateClick(date);
        setClickTimeout(null);
      }, 200);
      setClickTimeout(timeout);
    }
  };

  const days = generateDays();

  // Check if a date is in the selected week
  const isInSelectedWeek = (date: Date) => {
    if (viewMode !== 'week') return false;
    const weekStart = new Date(selectedDate);
    weekStart.setDate(selectedDate.getDate() - selectedDate.getDay());
    weekStart.setHours(0, 0, 0, 0);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);
    weekEnd.setHours(23, 59, 59, 999);
    return date >= weekStart && date <= weekEnd;
  };

  return (
    <div className="mini-calendar-sidebar">
      {/* Header */}
      <div className="mini-header">
        <button onClick={goToPreviousMonth} className="mini-nav-btn">
          <ChevronLeft size={14} />
        </button>
        <button onClick={goToToday} className="mini-month-label">
          {MONTHS[displayMonth]} {displayYear}
        </button>
        <button onClick={goToNextMonth} className="mini-nav-btn">
          <ChevronRight size={14} />
        </button>
      </div>

      {/* Calendar Grid */}
      <div className="mini-grid">
        {WEEKDAYS_SHORT.map((day, i) => (
          <div key={`h-${i}`} className="mini-weekday">{day}</div>
        ))}
        {days.map((day, i) => (
          <div
            key={i}
            className={`mini-day ${!day.isCurrentMonth ? 'other-month' : ''} ${day.isToday ? 'today' : ''} ${day.isSelected ? 'selected' : ''} ${isInSelectedWeek(day.date) ? 'in-week' : ''}`}
            onClick={() => handleClick(day.date)}
          >
            {day.date.getDate()}
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================================================
// MONTH VIEW COMPONENT
// ============================================================================

interface MonthViewProps {
  currentDate: Date;
  appointments: Appointment[];
  coachTimezone: string;
  externalEvents: ExternalEvent[];
  serviceColors: Record<string, string>;
  serviceNames: Record<string, string>;
  currentUserId: string;
  onDateClick: (date: Date) => void;
  onAppointmentClick?: (apt: Appointment, e: React.MouseEvent) => void;
  onAppointmentNavigate: (apt: Appointment) => void;
}

const MonthView: React.FC<MonthViewProps> = ({
  currentDate,
  appointments,
  coachTimezone,
  externalEvents,
  serviceColors,
  serviceNames,
  currentUserId,
  onDateClick,
  onAppointmentClick: _onAppointmentClick,
  onAppointmentNavigate
}) => {
  // Get the first and last day of the current month view
  const getMonthBounds = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    // Extend to include full weeks (Sunday start)
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - startDate.getDay());

    const endDate = new Date(lastDay);
    endDate.setDate(endDate.getDate() + (6 - endDate.getDay()));

    return { startDate, endDate, firstDay, lastDay };
  };

  // Generate calendar days
  const generateCalendarDays = () => {
    const { startDate, endDate, firstDay, lastDay } = getMonthBounds(currentDate);
    const days: { date: Date; isCurrentMonth: boolean; appointments: Appointment[] }[] = [];

    const current = new Date(startDate);
    while (current <= endDate) {
      const dateStr = current.toISOString().split('T')[0];
      const dayAppointments = appointments.filter(apt => {
        if (apt.all_day) {
          const aptStart = new Date(apt.start_time).toLocaleDateString('en-CA', { timeZone: coachTimezone });
          const aptEnd = new Date(apt.end_time).toLocaleDateString('en-CA', { timeZone: coachTimezone });
          return dateStr >= aptStart && dateStr <= aptEnd;
        }
        const aptDate = new Date(apt.start_time).toLocaleDateString('en-CA', { timeZone: coachTimezone });
        return aptDate === dateStr;
      });

      days.push({
        date: new Date(current),
        isCurrentMonth: current >= firstDay && current <= lastDay,
        appointments: dayAppointments
      });

      current.setDate(current.getDate() + 1);
    }

    return days;
  };

  // Format time for display in coach timezone
  const formatTime = (isoString: string) => {
    const date = new Date(isoString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: coachTimezone
    });
  };

  const isToday = (date: Date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const calendarDays = generateCalendarDays();

  return (
    <div className="month-view">
      {/* Day Headers */}
      <div className="day-headers">
        {WEEKDAYS_ABBR.map(day => (
          <div key={day} className="day-header">{day}</div>
        ))}
      </div>

      {/* Day Cells */}
      <div className="day-cells">
        {calendarDays.map((day, index) => (
          <div
            key={index}
            className={`day-cell ${!day.isCurrentMonth ? 'other-month' : ''} ${isToday(day.date) ? 'today' : ''}`}
            onClick={() => onDateClick(day.date)}
          >
            <div className="day-number">{day.date.getDate()}</div>
            <div className="day-appointments">
              {(() => {
                const dateStr = day.date.toLocaleDateString('en-CA');
                const dayExtEvents = externalEvents.filter(e => e.start_time.split('T')[0] === dateStr);

                // Merge MC appointments and external events into one list, sorted by time
                const allItems: { type: 'apt' | 'ext'; time: string; apt?: Appointment; ext?: ExternalEvent }[] = [
                  ...day.appointments.map(apt => ({ type: 'apt' as const, time: apt.start_time, apt })),
                  ...dayExtEvents.map(ext => ({ type: 'ext' as const, time: ext.start_time, ext }))
                ].sort((a, b) => a.time.localeCompare(b.time));

                const maxShow = 4;
                const overflow = allItems.length - maxShow;

                return (
                  <>
                    {allItems.slice(0, maxShow).map((item, i) =>
                      item.type === 'apt' ? (
                        <div
                          key={item.apt!.id}
                          className={`appointment-chip ${item.apt!.status}`}
                          style={{ '--apt-color': getAptColor(item.apt!, serviceColors), '--apt-border-color': getAptBorderColor(item.apt!, getAptColor(item.apt!, serviceColors), currentUserId), borderLeftColor: 'var(--apt-border-color)' } as React.CSSProperties}
                          title={getAptTooltip(item.apt!, serviceNames)}
                          onClick={(e) => { e.stopPropagation(); onAppointmentNavigate(item.apt!); }}
                        >
                          {!item.apt!.all_day && <span className="apt-time">{formatTime(item.apt!.start_time)}</span>}
                          <span className="apt-client">{item.apt!.all_day ? item.apt!.title : (item.apt!.event_type === 'meeting' ? item.apt!.title : getAptSubtitle(item.apt!))}</span>
                        </div>
                      ) : (
                        (() => {
                          const extBadge = item.ext!.provider ? PROVIDER_BADGES[item.ext!.provider] : null;
                          return (
                            <div
                              key={`ext-${i}`}
                              className="appointment-chip external-busy"
                              title={`${item.ext!.summary || 'Busy'}: ${formatTime(item.ext!.start_time)} - ${formatTime(item.ext!.end_time)}\nCalendar: ${item.ext!.calendar_name}${extBadge ? `\nSource: ${extBadge.label}` : ''}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                onAppointmentNavigate({ start_time: item.ext!.start_time } as Appointment);
                              }}
                            >
                              <span className="apt-time">{formatTime(item.ext!.start_time)}</span>
                              <span className="apt-client">{item.ext!.summary || 'Busy'}</span>
                              {extBadge && (
                                <span
                                  className="ext-provider-badge"
                                  style={{ backgroundColor: extBadge.bg, color: extBadge.fg }}
                                >
                                  {extBadge.letter}
                                </span>
                              )}
                            </div>
                          );
                        })()
                      )
                    )}
                    {overflow > 0 && (
                      <div className="more-appointments">+{overflow} more</div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================================================
// WEEK VIEW COMPONENT (with drag-to-move)
// ============================================================================

// Availability overlay helper — renders shaded blocks on week/day views
type AvailWindow = { day_of_week: number; start_time: string; end_time: string; is_active: boolean; services?: string[] };

function renderAvailabilityOverlays(date: Date, windows: AvailWindow[], timezone: string, svcNames?: Record<string, string>) {
  const dayOfWeek = date.getDay();
  const dayWindows = windows.filter(w => w.day_of_week === dayOfWeek && w.is_active);
  if (dayWindows.length === 0) return null;

  // Merge overlapping windows with the same time range — combine their services
  const merged = new Map<string, { start_time: string; end_time: string; services: string[] }>();
  for (const w of dayWindows) {
    const key = `${w.start_time}-${w.end_time}`;
    const existing = merged.get(key);
    if (existing) {
      for (const svc of (w.services || [])) {
        if (!existing.services.includes(svc)) existing.services.push(svc);
      }
    } else {
      merged.set(key, { start_time: w.start_time, end_time: w.end_time, services: [...(w.services || [])] });
    }
  }

  return [...merged.values()].map((w, i) => {
    const [startH, startM] = w.start_time.split(':').map(Number);
    const [endH, endM] = w.end_time.split(':').map(Number);

    const startUTC = new Date(date);
    startUTC.setUTCHours(startH, startM, 0, 0);
    const endUTC = new Date(date);
    endUTC.setUTCHours(endH, endM, 0, 0);

    const startLocal = new Date(startUTC.toLocaleString('en-US', { timeZone: timezone }));
    const endLocal = new Date(endUTC.toLocaleString('en-US', { timeZone: timezone }));

    const startHour = startLocal.getHours() + startLocal.getMinutes() / 60;
    const endHour = endLocal.getHours() + endLocal.getMinutes() / 60;

    const top = (startHour - START_HOUR) * PX_PER_HOUR;
    const height = (endHour - startHour) * PX_PER_HOUR;
    if (height <= 0 || top + height < 0) return null;

    const startLabel = startLocal.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    const endLabel = endLocal.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    const svcList = w.services.length > 0
      ? w.services.map(id => svcNames?.[id] || id).join(', ')
      : 'All services';
    const tooltip = `Available: ${startLabel} - ${endLabel}\nServices: ${svcList}`;

    return (
      <div
        key={`avail-${i}`}
        className="availability-overlay"
        title={tooltip}
        style={{ position: 'absolute', top: `${top}px`, height: `${height}px`, left: 0, right: 0, zIndex: 1 }}
      >
        <div className="avail-label" />
      </div>
    );
  });
}

// Blocked time overlay — renders recurring blocks as red diagonal-stripe overlays
type BlockedTimeEntry = {
  id: string; coach_id: string; recurring?: boolean; day_of_week?: number;
  start_time: string; end_time: string; reason: string; type: string; all_day: boolean;
};

function renderBlockedTimeOverlays(date: Date, blocks: BlockedTimeEntry[], timezone: string) {
  const dayOfWeek = date.getDay();
  const dayBlocks = blocks.filter(b => b.recurring && b.day_of_week === dayOfWeek);
  if (dayBlocks.length === 0) return null;

  return dayBlocks.map((b, i) => {
    const [startH, startM] = b.start_time.split(':').map(Number);
    const [endH, endM] = b.end_time.split(':').map(Number);

    const startUTC = new Date(date);
    startUTC.setUTCHours(startH, startM, 0, 0);
    const endUTC = new Date(date);
    endUTC.setUTCHours(endH, endM, 0, 0);

    const startLocal = new Date(startUTC.toLocaleString('en-US', { timeZone: timezone }));
    const endLocal = new Date(endUTC.toLocaleString('en-US', { timeZone: timezone }));

    const startHour = startLocal.getHours() + startLocal.getMinutes() / 60;
    const endHour = endLocal.getHours() + endLocal.getMinutes() / 60;

    const top = (startHour - START_HOUR) * PX_PER_HOUR;
    const height = (endHour - startHour) * PX_PER_HOUR;
    if (height <= 0 || top + height < 0) return null;

    const startLabel = startLocal.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    const endLabel = endLocal.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    const tooltip = `Blocked: ${startLabel} - ${endLabel}${b.reason ? `\n${b.reason}` : ''}`;

    return (
      <div
        key={`block-${i}`}
        className="blocked-time-overlay"
        title={tooltip}
        style={{ position: 'absolute', top: `${top}px`, height: `${height}px`, left: 0, right: 0, zIndex: 1 }}
      />
    );
  });
}

// External event blocking overlay — renders busy blocks from synced calendars
type ExternalEvent = { start_time: string; end_time: string; status: string; calendar_name: string; all_day: boolean; summary?: string; provider?: 'google' | 'apple' };

// Provider visual config: letter badge + brand color (descriptive, not trademarked marks)
const PROVIDER_BADGES: Record<string, { letter: string; bg: string; fg: string; label: string }> = {
  google: { letter: 'G', bg: '#4285F4', fg: '#ffffff', label: 'Google Calendar' },
  apple: { letter: 'A', bg: '#A2AAAD', fg: '#ffffff', label: 'Apple iCloud' }
};

function renderExternalEventOverlays(date: Date, events: ExternalEvent[], timezone: string) {
  const dateStr = date.toLocaleDateString('en-CA'); // YYYY-MM-DD
  const dayEvents = events.filter(e => {
    const eDate = e.start_time.split('T')[0];
    return eDate === dateStr;
  });
  if (dayEvents.length === 0) return null;

  return dayEvents.map((evt, i) => {
    const startUTC = new Date(evt.start_time);
    const endUTC = new Date(evt.end_time);

    const startLocal = new Date(startUTC.toLocaleString('en-US', { timeZone: timezone }));
    const endLocal = new Date(endUTC.toLocaleString('en-US', { timeZone: timezone }));

    const startHour = startLocal.getHours() + startLocal.getMinutes() / 60;
    const endHour = endLocal.getHours() + endLocal.getMinutes() / 60;

    const top = (startHour - START_HOUR) * PX_PER_HOUR;
    const height = Math.max((endHour - startHour) * PX_PER_HOUR, 15);
    if (top + height < 0) return null;

    const startLabel = startLocal.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    const endLabel = endLocal.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    const displayName = evt.summary || 'Busy';
    const badge = evt.provider ? PROVIDER_BADGES[evt.provider] : null;
    const tooltip = `${displayName}: ${startLabel} - ${endLabel}\nCalendar: ${evt.calendar_name}${badge ? `\nSource: ${badge.label}` : ''}`;

    return (
      <div
        key={`ext-${i}`}
        className="external-event-overlay"
        title={tooltip}
        style={{ position: 'absolute', top: `${top}px`, height: `${height}px`, left: '4px', right: '24px', zIndex: 1 }}
      >
        <span className="ext-label">{displayName}</span>
        {badge && (
          <span
            className="ext-provider-badge"
            style={{ backgroundColor: badge.bg, color: badge.fg }}
          >
            {badge.letter}
          </span>
        )}
      </div>
    );
  });
}

interface WeekViewProps {
  currentDate: Date;
  appointments: Appointment[];
  coachTimezone: string;
  availabilityWindows: AvailWindow[];
  externalEvents: ExternalEvent[];
  blockedTime: BlockedTimeEntry[];
  serviceNames: Record<string, string>;
  serviceColors: Record<string, string>;
  currentUserId: string;
  onAppointmentClick: (apt: Appointment, e: React.MouseEvent) => void;
  onTimeSlotClick: (date: Date, hour: number) => void;
  onAppointmentMove: (apt: Appointment, newStart: Date) => void;
  onAppointmentResize: (apt: Appointment, newStart: Date, newEnd: Date) => void;
}

const WeekView: React.FC<WeekViewProps> = ({
  currentDate,
  appointments,
  coachTimezone,
  availabilityWindows,
  externalEvents,
  serviceNames,
  serviceColors,
  currentUserId,
  blockedTime,
  onAppointmentClick,
  onTimeSlotClick,
  onAppointmentMove,
  onAppointmentResize
}) => {
  // Refs for measuring column positions
  const gridRef = useRef<HTMLDivElement>(null);
  const columnRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Current time marker — updates every minute
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  const getCurrentTimePosition = () => {
    const localNow = new Date(now.toLocaleString('en-US', { timeZone: coachTimezone }));
    const hour = localNow.getHours() + localNow.getMinutes() / 60;
    return (hour - START_HOUR) * PX_PER_HOUR;
  };

  // Auto-scroll to 7 AM (or current hour) on mount
  useEffect(() => {
    const grid = gridRef.current;
    if (!grid) return;
    const now = new Date();
    const currentHour = now.getHours();
    const scrollToHour = Math.max(currentHour - 1, 7); // Show 1 hour before current, minimum 7 AM
    grid.scrollTop = (scrollToHour - START_HOUR) * PX_PER_HOUR;
  }, []);

  // Drag state - only set when actually dragging (past threshold)
  const [dragState, setDragState] = useState<{
    isDragging: boolean;
    apt: Appointment;
    startX: number;
    startY: number;
    currentX: number;
    currentY: number;
    originalDayIndex: number;
    originalTop: number;
  } | null>(null);

  // Ref for potential drag start - doesn't cause re-render
  const potentialDragRef = useRef<{
    apt: Appointment;
    startX: number;
    startY: number;
    originalDayIndex: number;
    originalTop: number;
  } | null>(null);

  const DRAG_THRESHOLD = 5; // Pixels mouse must move before drag starts

  // Resize state - for adjusting start/end by dragging top/bottom edges
  const [resizeState, setResizeState] = useState<{
    edge: 'top' | 'bottom';
    apt: Appointment;
    startY: number;
    originalStartHour: number;
    originalEndHour: number;
    currentStartHour: number;
    currentEndHour: number;
  } | null>(null);

  // Ref to prevent click after resize
  const justFinishedResizingRef = useRef(false);

  // Get week bounds (Sunday to Saturday)
  const getWeekBounds = () => {
    const start = new Date(currentDate);
    start.setDate(currentDate.getDate() - currentDate.getDay());
    start.setHours(0, 0, 0, 0);

    const days: Date[] = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(start);
      day.setDate(start.getDate() + i);
      days.push(day);
    }

    return days;
  };

  const weekDays = getWeekBounds();

  // Get appointments for a specific day
  const getAppointmentsForDay = (date: Date) => {
    const dateStr = date.toLocaleDateString('en-CA', { timeZone: coachTimezone });
    return appointments.filter(apt => {
      if (apt.all_day) {
        // Multi-day all-day events: show on every day they span
        const aptStart = new Date(apt.start_time).toLocaleDateString('en-CA', { timeZone: coachTimezone });
        const aptEnd = new Date(apt.end_time).toLocaleDateString('en-CA', { timeZone: coachTimezone });
        return dateStr >= aptStart && dateStr <= aptEnd;
      }
      const aptDate = new Date(apt.start_time).toLocaleDateString('en-CA', { timeZone: coachTimezone });
      return aptDate === dateStr;
    });
  };

  // Calculate overlap layout for a day's appointments (Outlook-style column layout)
  const getOverlapLayout = (dayAppts: Appointment[]) => {
    const layout = new Map<string, { column: number; totalColumns: number }>();
    const sorted = [...dayAppts].sort((a, b) => a.start_time.localeCompare(b.start_time));
    const groups: Appointment[][] = [];

    for (const apt of sorted) {
      const aptStart = new Date(apt.start_time).getTime();
      const aptEnd = new Date(apt.end_time).getTime();
      let placed = false;
      for (const group of groups) {
        if (group.some(g => aptStart < new Date(g.end_time).getTime() && aptEnd > new Date(g.start_time).getTime())) {
          group.push(apt);
          placed = true;
          break;
        }
      }
      if (!placed) groups.push([apt]);
    }

    for (const group of groups) {
      const columns: Appointment[][] = [];
      for (const apt of group) {
        const aptStart = new Date(apt.start_time).getTime();
        let col = 0;
        while (col < columns.length) {
          const lastInCol = columns[col][columns[col].length - 1];
          if (new Date(lastInCol.end_time).getTime() <= aptStart) break;
          col++;
        }
        if (col >= columns.length) columns.push([]);
        columns[col].push(apt);
        layout.set(apt.id, { column: col, totalColumns: 0 });
      }
      for (const apt of group) {
        layout.get(apt.id)!.totalColumns = columns.length;
      }
    }
    return layout;
  };

  // Calculate position and height for an appointment block
  const getAppointmentStyle = (apt: Appointment, _dayIndex: number, overlap?: { column: number; totalColumns: number }) => {
    const start = new Date(apt.start_time);
    const localStart = new Date(start.toLocaleString('en-US', { timeZone: coachTimezone }));
    const startHour = localStart.getHours() + localStart.getMinutes() / 60;

    // Position relative to 5am start
    const top = (startHour - START_HOUR) * PX_PER_HOUR;
    const height = apt.duration_minutes * (PX_PER_HOUR / 60);

    // Overlap positioning
    const col = overlap?.column ?? 0;
    const total = overlap?.totalColumns ?? 1;
    const widthPercent = 100 / total;
    const leftPercent = col * widthPercent;

    // If this appointment is being resized, show preview
    if (resizeState && resizeState.apt.id === apt.id) {
      const previewTop = (resizeState.currentStartHour - START_HOUR) * PX_PER_HOUR;
      const previewHeight = (resizeState.currentEndHour - resizeState.currentStartHour) * PX_PER_HOUR;

      return {
        top: `${previewTop}px`,
        height: `${Math.max(previewHeight, 15)}px`,
        minHeight: '15px',
        left: `${leftPercent}%`,
        width: `${widthPercent}%`,
        zIndex: 100,
        opacity: 0.9,
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
      };
    }

    // If this appointment is being dragged, apply offset
    if (dragState?.isDragging && dragState.apt?.id === apt.id) {
      const deltaX = dragState.currentX - dragState.startX;
      const deltaY = dragState.currentY - dragState.startY;

      return {
        top: `${dragState.originalTop + deltaY}px`,
        height: `${height}px`,
        minHeight: '20px',
        left: `${leftPercent}%`,
        width: `${widthPercent}%`,
        transform: `translateX(${deltaX}px)`,
        zIndex: 100,
        opacity: 0.9,
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
      };
    }

    return {
      top: `${top}px`,
      height: `${height}px`,
      minHeight: '20px',
      left: `${leftPercent}%`,
      width: `${widthPercent}%`
    };
  };

  // Format time for display
  const formatTime = (isoString: string) => {
    const date = new Date(isoString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: coachTimezone
    });
  };

  const isToday = (date: Date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  // Start potential drag - store in ref (no state change = no re-render = double-click works)
  const handleMouseDown = (e: React.MouseEvent, apt: Appointment, dayIndex: number) => {
    // Only handle left mouse button
    if (e.button !== 0) return;

    // If we just finished resizing, don't start a drag
    if (justFinishedResizingRef.current) {
      justFinishedResizingRef.current = false;
      return;
    }

    // Don't call e.preventDefault() here - it blocks double-click detection
    // Text selection is prevented via CSS user-select: none when dragging

    const start = new Date(apt.start_time);
    const localStart = new Date(start.toLocaleString('en-US', { timeZone: coachTimezone }));
    const startHour = localStart.getHours() + localStart.getMinutes() / 60;
    const originalTop = (startHour - START_HOUR) * PX_PER_HOUR;

    // Store in ref - doesn't trigger re-render, allows double-click to work
    potentialDragRef.current = {
      apt,
      startX: e.clientX,
      startY: e.clientY,
      originalDayIndex: dayIndex,
      originalTop
    };
  };

  // Start resize operation
  const handleResizeMouseDown = (e: React.MouseEvent, apt: Appointment, edge: 'top' | 'bottom') => {
    e.stopPropagation(); // Don't trigger drag
    e.preventDefault();

    const start = new Date(apt.start_time);
    const end = new Date(apt.end_time);
    const localStart = new Date(start.toLocaleString('en-US', { timeZone: coachTimezone }));
    const localEnd = new Date(end.toLocaleString('en-US', { timeZone: coachTimezone }));

    const startHour = localStart.getHours() + localStart.getMinutes() / 60;
    const endHour = localEnd.getHours() + localEnd.getMinutes() / 60;

    setResizeState({
      edge,
      apt,
      startY: e.clientY,
      originalStartHour: startHour,
      originalEndHour: endHour,
      currentStartHour: startHour,
      currentEndHour: endHour
    });
  };

  // Global mouse listeners for drag detection and handling
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      // If actively dragging, update position
      if (dragState?.isDragging) {
        setDragState(prev => prev ? {
          ...prev,
          currentX: e.clientX,
          currentY: e.clientY
        } : null);
        return;
      }

      // Check if we have a potential drag that should start
      const potential = potentialDragRef.current;
      if (potential) {
        const deltaX = e.clientX - potential.startX;
        const deltaY = e.clientY - potential.startY;
        const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

        // Start actual drag if threshold exceeded
        if (distance > DRAG_THRESHOLD) {
          setDragState({
            isDragging: true,
            apt: potential.apt,
            startX: potential.startX,
            startY: potential.startY,
            currentX: e.clientX,
            currentY: e.clientY,
            originalDayIndex: potential.originalDayIndex,
            originalTop: potential.originalTop
          });
          potentialDragRef.current = null;
        }
      }
    };

    const handleMouseUp = (e: MouseEvent) => {
      // Clear potential drag ref on any mouseup
      potentialDragRef.current = null;

      // If not actively dragging, nothing to do
      if (!dragState?.isDragging || !gridRef.current) {
        setDragState(null);
        return;
      }

      // Calculate which day column we're over
      let targetDayIndex = dragState.originalDayIndex;
      const targetTop = dragState.originalTop + (dragState.currentY - dragState.startY);

      // Find which column the mouse is over
      for (let i = 0; i < columnRefs.current.length; i++) {
        const col = columnRefs.current[i];
        if (col) {
          const rect = col.getBoundingClientRect();
          if (e.clientX >= rect.left && e.clientX <= rect.right) {
            targetDayIndex = i;
            break;
          }
        }
      }

      // Snap to 15-minute increments (15px = 15 minutes)
      const snapSize = PX_PER_HOUR / 4; // 15-minute snap
      const snappedTop = Math.round(targetTop / snapSize) * snapSize;

      // Clamp within valid time range (24h * 60px = 1440)
      const clampedTop = Math.max(0, Math.min(snappedTop, TOTAL_HOURS * PX_PER_HOUR - dragState.apt.duration_minutes * (PX_PER_HOUR / 60)));

      // Calculate the new hour from top position
      const newHour = START_HOUR + (clampedTop / PX_PER_HOUR);
      const newHours = Math.floor(newHour);
      const newMinutes = Math.round((newHour - newHours) * 60);

      // Build new start date
      const targetDay = weekDays[targetDayIndex];
      const newStart = new Date(targetDay);
      newStart.setHours(newHours, newMinutes, 0, 0);

      // Check if position actually changed
      const originalStart = new Date(dragState.apt.start_time);
      const originalLocalStart = new Date(originalStart.toLocaleString('en-US', { timeZone: coachTimezone }));

      const hasChanged =
        targetDayIndex !== dragState.originalDayIndex ||
        newHours !== originalLocalStart.getHours() ||
        newMinutes !== originalLocalStart.getMinutes();

      if (hasChanged) {
        onAppointmentMove(dragState.apt, newStart);
      }

      setDragState(null);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragState, weekDays, onAppointmentMove, coachTimezone]);

  // Global mouse listeners for resize handling
  useEffect(() => {
    if (!resizeState) return;

    const handleResizeMouseMove = (e: MouseEvent) => {
      const deltaY = e.clientY - resizeState.startY;
      const deltaHours = deltaY / PX_PER_HOUR;

      // Snap to 15-minute increments
      const snappedDelta = Math.round(deltaHours * 4) / 4;

      let newStartHour = resizeState.originalStartHour;
      let newEndHour = resizeState.originalEndHour;

      if (resizeState.edge === 'top') {
        // Moving start time
        newStartHour = resizeState.originalStartHour + snappedDelta;
        // Clamp: can't go before 5am, can't pass end time minus 15 min
        newStartHour = Math.max(START_HOUR, Math.min(newStartHour, newEndHour - 0.25));
      } else {
        // Moving end time
        newEndHour = resizeState.originalEndHour + snappedDelta;
        // Clamp: can't go after 9pm (21), can't go before start time plus 15 min
        newEndHour = Math.max(newStartHour + 0.25, Math.min(newEndHour, START_HOUR + TOTAL_HOURS));
      }

      setResizeState(prev => prev ? {
        ...prev,
        currentStartHour: newStartHour,
        currentEndHour: newEndHour
      } : null);
    };

    const handleResizeMouseUp = () => {
      if (!resizeState) return;

      // Check if anything changed
      const startChanged = resizeState.currentStartHour !== resizeState.originalStartHour;
      const endChanged = resizeState.currentEndHour !== resizeState.originalEndHour;

      if (startChanged || endChanged) {
        // Build new start and end times
        // Get the original date from the appointment
        const originalStart = new Date(resizeState.apt.start_time);
        const localDate = new Date(originalStart.toLocaleString('en-US', { timeZone: coachTimezone }));

        const newStartHours = Math.floor(resizeState.currentStartHour);
        const newStartMinutes = Math.round((resizeState.currentStartHour - newStartHours) * 60);

        const newEndHours = Math.floor(resizeState.currentEndHour);
        const newEndMinutes = Math.round((resizeState.currentEndHour - newEndHours) * 60);

        const newStart = new Date(localDate);
        newStart.setHours(newStartHours, newStartMinutes, 0, 0);

        const newEnd = new Date(localDate);
        newEnd.setHours(newEndHours, newEndMinutes, 0, 0);

        onAppointmentResize(resizeState.apt, newStart, newEnd);
      }

      // Mark that we just finished resizing to prevent drag start
      justFinishedResizingRef.current = true;
      setTimeout(() => {
        justFinishedResizingRef.current = false;
      }, 100);

      setResizeState(null);
    };

    window.addEventListener('mousemove', handleResizeMouseMove);
    window.addEventListener('mouseup', handleResizeMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleResizeMouseMove);
      window.removeEventListener('mouseup', handleResizeMouseUp);
    };
  }, [resizeState, coachTimezone, onAppointmentResize]);

  return (
    <div className={`week-view ${dragState?.isDragging ? 'dragging' : ''} ${resizeState ? 'resizing' : ''}`}>
      {/* Day Headers */}
      <div className="week-header">
        <div className="time-gutter-header"></div>
        {weekDays.map((day, i) => (
          <div key={i} className={`week-day-header ${isToday(day) ? 'today' : ''}`}>
            <span className="day-date">{day.getDate()}</span>
            <span className="day-name">{WEEKDAYS[day.getDay()]}</span>
          </div>
        ))}
      </div>

      {/* All-Day Events Banner */}
      {(() => {
        // Collect unique all-day events and compute their column spans
        const allDayEvents: Array<{ apt: Appointment; startCol: number; span: number }> = [];
        const seen = new Set<string>();
        weekDays.forEach((day, colIdx) => {
          const dayAppts = getAppointmentsForDay(day).filter(a => a.all_day);
          dayAppts.forEach(apt => {
            if (seen.has(apt.id)) return;
            seen.add(apt.id);
            // Calculate how many columns this event spans
            let span = 1;
            for (let c = colIdx + 1; c < weekDays.length; c++) {
              const nextDateStr = weekDays[c].toLocaleDateString('en-CA', { timeZone: coachTimezone });
              const aptEnd = new Date(apt.end_time).toLocaleDateString('en-CA', { timeZone: coachTimezone });
              if (nextDateStr <= aptEnd) span++;
              else break;
            }
            allDayEvents.push({ apt, startCol: colIdx, span });
          });
        });

        if (allDayEvents.length === 0) return null;
        return (
          <div className="allday-banner">
            <div className="allday-gutter-label">all-day</div>
            <div className="allday-grid">
              {allDayEvents.map(({ apt, startCol, span }) => (
                <div
                  key={apt.id}
                  className={`allday-chip ${apt.event_type || 'appointment'}`}
                  style={{
                    gridColumn: `${startCol + 1} / span ${span}`,
                    '--apt-color': getAptColor(apt, serviceColors),
                    '--apt-border-color': getAptBorderColor(apt, getAptColor(apt, serviceColors), currentUserId),
                    borderLeftColor: 'var(--apt-border-color)'
                  } as React.CSSProperties}
                  title={getAptTooltip(apt, serviceNames)}
                  onDoubleClick={(e) => onAppointmentClick(apt, e)}
                >
                  {apt.title}
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Time Grid */}
      <div className="week-grid" ref={gridRef}>
        {/* Time Gutter */}
        <div className="time-gutter">
          {TIME_SLOTS.map((slot, i) => (
            <div key={i} className={`time-label ${slot.isHalfHour ? 'half-hour' : 'on-hour'}`}>
              {slot.label}
            </div>
          ))}
        </div>

        {/* Day Columns */}
        {weekDays.map((day, dayIndex) => (
          <div
            key={dayIndex}
            className={`day-column ${isToday(day) ? 'today' : ''}`}
            ref={el => columnRefs.current[dayIndex] = el}
          >
            {/* Time slot backgrounds (30-min intervals with 15-min sub-line) */}
            {TIME_SLOTS.map((slot, i) => (
              <div
                key={i}
                className={`time-slot ${slot.isHalfHour ? 'on-hour' : 'half-hour'}`}
                onClick={() => !dragState?.isDragging && onTimeSlotClick(day, slot.fractionalHour)}
              />
            ))}

            {/* Availability window shading */}
            {renderAvailabilityOverlays(day, availabilityWindows, coachTimezone, serviceNames)}

            {/* Blocked time overlays (red) */}
            {renderBlockedTimeOverlays(day, blockedTime, coachTimezone)}

            {/* External calendar busy blocks */}
            {renderExternalEventOverlays(day, externalEvents, coachTimezone)}

            {/* Current time marker */}
            {isToday(day) && (
              <div className="current-time-marker" style={{ top: `${getCurrentTimePosition()}px` }}>
                <div className="time-marker-dot" />
                <div className="time-marker-line" />
              </div>
            )}

            {/* Appointments - drag to move, resize handles on top/bottom, double-click to open */}
            <div className="appointments-container">
              {(() => {
                const dayAppts = getAppointmentsForDay(day).filter(a => !a.all_day);
                const layout = getOverlapLayout(dayAppts);
                return dayAppts.map(apt => (
                <div
                  key={apt.id}
                  className={`week-appointment ${apt.status} ${apt.event_type || 'appointment'} ${getViewerBorderClasses(apt, currentUserId)} ${dragState?.apt?.id === apt.id ? 'dragging' : ''} ${resizeState?.apt?.id === apt.id ? 'resizing' : ''}`}
                  style={{ ...getAppointmentStyle(apt, dayIndex, layout.get(apt.id)), '--apt-color': getAptColor(apt, serviceColors), '--apt-border-color': getAptBorderColor(apt, getAptColor(apt, serviceColors), currentUserId), borderLeftColor: 'var(--apt-border-color)' } as React.CSSProperties}
                  title={getAptTooltip(apt, serviceNames)}
                  draggable={false}
                  onDragStart={(e) => e.preventDefault()}
                  onMouseDown={(e) => handleMouseDown(e, apt, dayIndex)}
                  onDoubleClick={(e) => !dragState?.isDragging && !resizeState && onAppointmentClick(apt, e)}
                >
                  {/* Top resize handle - adjust start time */}
                  <div
                    className="resize-handle top"
                    onMouseDown={(e) => handleResizeMouseDown(e, apt, 'top')}
                  />
                  <div className="apt-content">
                    <div className="apt-time">{formatTime(apt.start_time)}</div>
                    <div className="apt-title">{apt.title}</div>
                    <div className="apt-client">{getAptSubtitle(apt)}</div>
                  </div>
                  {/* Bottom resize handle - adjust end time */}
                  <div
                    className="resize-handle bottom"
                    onMouseDown={(e) => handleResizeMouseDown(e, apt, 'bottom')}
                  />
                </div>
              ));
              })()}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================================================
// DAY VIEW COMPONENT (with drag-to-move and resize)
// ============================================================================

interface DayViewProps {
  currentDate: Date;
  appointments: Appointment[];
  coachTimezone: string;
  availabilityWindows: AvailWindow[];
  externalEvents: ExternalEvent[];
  blockedTime: BlockedTimeEntry[];
  serviceNames: Record<string, string>;
  serviceColors: Record<string, string>;
  currentUserId: string;
  onAppointmentClick: (apt: Appointment, e: React.MouseEvent) => void;
  onTimeSlotClick: (date: Date, hour: number) => void;
  onAppointmentMove: (apt: Appointment, newStart: Date) => void;
  onAppointmentResize: (apt: Appointment, newStart: Date, newEnd: Date) => void;
}

const DayView: React.FC<DayViewProps> = ({
  currentDate,
  appointments,
  coachTimezone,
  availabilityWindows,
  externalEvents,
  serviceNames,
  serviceColors,
  blockedTime,
  currentUserId,
  onAppointmentClick,
  onTimeSlotClick,
  onAppointmentMove,
  onAppointmentResize
}) => {
  const dateStr = currentDate.toLocaleDateString('en-CA', { timeZone: coachTimezone });
  const allDayAppointments = appointments.filter(apt => {
    const aptDate = new Date(apt.start_time).toLocaleDateString('en-CA', { timeZone: coachTimezone });
    return aptDate === dateStr;
  });
  const dayAppointments = allDayAppointments.filter(a => !a.all_day);
  const dayAllDayEvents = allDayAppointments.filter(a => a.all_day);

  const dayGridRef = useRef<HTMLDivElement>(null);
  const columnRef = useRef<HTMLDivElement>(null);

  // Day view uses taller slots for better readability
  const DAY_PX = 120; // 120px per hour vs 80px on week view

  // Auto-scroll to 7 AM (or current hour) on mount
  useEffect(() => {
    const grid = dayGridRef.current;
    if (!grid) return;
    const now = new Date();
    const scrollToHour = Math.max(now.getHours() - 1, 7);
    grid.scrollTop = (scrollToHour - START_HOUR) * DAY_PX;
  }, []);

  // Drag state
  const [dragState, setDragState] = useState<{
    apt: Appointment; startY: number; originalTop: number; isDragging: boolean;
  } | null>(null);
  const potentialDragRef = useRef<{ apt: Appointment; startY: number; originalTop: number } | null>(null);

  // Resize state
  const [resizeState, setResizeState] = useState<{
    edge: 'top' | 'bottom'; apt: Appointment; startY: number;
    originalStartHour: number; originalEndHour: number;
    currentStartHour: number; currentEndHour: number;
  } | null>(null);

  // Calculate overlap layout for appointments
  const overlapLayout = (() => {
    const layout = new Map<string, { column: number; totalColumns: number }>();
    const sorted = [...dayAppointments].sort((a, b) => a.start_time.localeCompare(b.start_time));
    const groups: Appointment[][] = [];

    for (const apt of sorted) {
      const aptStart = new Date(apt.start_time).getTime();
      const aptEnd = new Date(apt.end_time).getTime();

      // Find a group this appointment overlaps with
      let placed = false;
      for (const group of groups) {
        const overlaps = group.some(g => {
          const gStart = new Date(g.start_time).getTime();
          const gEnd = new Date(g.end_time).getTime();
          return aptStart < gEnd && aptEnd > gStart;
        });
        if (overlaps) {
          group.push(apt);
          placed = true;
          break;
        }
      }
      if (!placed) groups.push([apt]);
    }

    // Assign columns within each group
    for (const group of groups) {
      const columns: Appointment[][] = [];
      for (const apt of group) {
        const aptStart = new Date(apt.start_time).getTime();
        let col = 0;
        while (col < columns.length) {
          const lastInCol = columns[col][columns[col].length - 1];
          if (new Date(lastInCol.end_time).getTime() <= aptStart) break;
          col++;
        }
        if (col >= columns.length) columns.push([]);
        columns[col].push(apt);
        layout.set(apt.id, { column: col, totalColumns: 0 });
      }
      // Set total columns for all in group
      for (const apt of group) {
        const l = layout.get(apt.id)!;
        l.totalColumns = columns.length;
      }
    }

    return layout;
  })();

  const getAppointmentStyle = (apt: Appointment) => {
    const start = new Date(apt.start_time);
    const localStart = new Date(start.toLocaleString('en-US', { timeZone: coachTimezone }));
    const startHour = localStart.getHours() + localStart.getMinutes() / 60;
    const top = (startHour - START_HOUR) * DAY_PX;
    const height = apt.duration_minutes * (DAY_PX / 60);

    // Overlap positioning
    const overlap = overlapLayout.get(apt.id) || { column: 0, totalColumns: 1 };
    const widthPercent = 100 / overlap.totalColumns;
    const leftPercent = overlap.column * widthPercent;

    if (resizeState && resizeState.apt.id === apt.id) {
      const previewTop = (resizeState.currentStartHour - START_HOUR) * DAY_PX;
      const previewHeight = (resizeState.currentEndHour - resizeState.currentStartHour) * DAY_PX;
      return { top: `${previewTop}px`, height: `${Math.max(previewHeight, 15)}px`, left: `${leftPercent}%`, width: `${widthPercent}%`, zIndex: 100, opacity: 0.9 };
    }

    if (dragState?.isDragging && dragState.apt.id === apt.id) {
      return { top: `${dragState.originalTop}px`, height: `${height}px`, left: `${leftPercent}%`, width: `${widthPercent}%`, zIndex: 100, opacity: 0.9 };
    }

    return { top: `${top}px`, height: `${height}px`, minHeight: '30px', left: `${leftPercent}%`, width: `${widthPercent}%` };
  };

  // Drag handlers
  const handleMouseDown = (e: React.MouseEvent, apt: Appointment) => {
    if (e.button !== 0) return;
    const start = new Date(apt.start_time);
    const localStart = new Date(start.toLocaleString('en-US', { timeZone: coachTimezone }));
    const startHour = localStart.getHours() + localStart.getMinutes() / 60;
    const originalTop = (startHour - START_HOUR) * DAY_PX;
    potentialDragRef.current = { apt, startY: e.clientY, originalTop };
  };

  // Resize handlers
  const handleResizeMouseDown = (e: React.MouseEvent, apt: Appointment, edge: 'top' | 'bottom') => {
    e.stopPropagation();
    const start = new Date(apt.start_time);
    const end = new Date(apt.end_time);
    const localStart = new Date(start.toLocaleString('en-US', { timeZone: coachTimezone }));
    const localEnd = new Date(end.toLocaleString('en-US', { timeZone: coachTimezone }));
    const startHour = localStart.getHours() + localStart.getMinutes() / 60;
    const endHour = localEnd.getHours() + localEnd.getMinutes() / 60;
    setResizeState({ edge, apt, startY: e.clientY, originalStartHour: startHour, originalEndHour: endHour, currentStartHour: startHour, currentEndHour: endHour });
  };

  // Global mouse listeners for drag
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (potentialDragRef.current && !dragState) {
        const delta = Math.abs(e.clientY - potentialDragRef.current.startY);
        if (delta > 5) {
          setDragState({ ...potentialDragRef.current, isDragging: true, startY: e.clientY });
        }
        return;
      }
      if (!dragState?.isDragging) return;

      const grid = dayGridRef.current;
      if (!grid) return;
      const rect = grid.getBoundingClientRect();
      const relY = e.clientY - rect.top + grid.scrollTop;
      const snapSize = DAY_PX / 4;
      const snappedTop = Math.round(relY / snapSize) * snapSize;
      const clampedTop = Math.max(0, Math.min(snappedTop, TOTAL_HOURS * DAY_PX - dragState.apt.duration_minutes * (DAY_PX / 60)));

      setDragState(prev => prev ? { ...prev, originalTop: clampedTop } : null);
    };

    const handleMouseUp = () => {
      if (dragState?.isDragging) {
        const newHour = START_HOUR + (dragState.originalTop / DAY_PX);
        const newHours = Math.floor(newHour);
        const newMinutes = Math.round((newHour - newHours) * 60);
        const newStart = new Date(currentDate);
        newStart.setHours(newHours, newMinutes, 0, 0);
        // Convert from local to UTC
        const tzOffset = newStart.getTime() - new Date(newStart.toLocaleString('en-US', { timeZone: coachTimezone })).getTime();
        const correctedStart = new Date(newStart.getTime() + tzOffset);
        onAppointmentMove(dragState.apt, correctedStart);
      }
      potentialDragRef.current = null;
      setDragState(null);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => { window.removeEventListener('mousemove', handleMouseMove); window.removeEventListener('mouseup', handleMouseUp); };
  }, [dragState, currentDate, onAppointmentMove, coachTimezone]);

  // Global mouse listeners for resize
  useEffect(() => {
    if (!resizeState) return;

    const handleResizeMove = (e: MouseEvent) => {
      const deltaHours = (e.clientY - resizeState.startY) / DAY_PX;
      const snappedDelta = Math.round(deltaHours * 4) / 4;
      let newStart = resizeState.originalStartHour;
      let newEnd = resizeState.originalEndHour;

      if (resizeState.edge === 'top') {
        newStart = Math.max(START_HOUR, Math.min(resizeState.originalStartHour + snappedDelta, newEnd - 0.25));
      } else {
        newEnd = Math.max(newStart + 0.25, Math.min(resizeState.originalEndHour + snappedDelta, START_HOUR + TOTAL_HOURS));
      }
      setResizeState(prev => prev ? { ...prev, currentStartHour: newStart, currentEndHour: newEnd } : null);
    };

    const handleResizeUp = () => {
      const makeDate = (hour: number) => {
        const h = Math.floor(hour);
        const m = Math.round((hour - h) * 60);
        const d = new Date(currentDate);
        d.setHours(h, m, 0, 0);
        const tzOffset = d.getTime() - new Date(d.toLocaleString('en-US', { timeZone: coachTimezone })).getTime();
        return new Date(d.getTime() + tzOffset);
      };
      onAppointmentResize(resizeState.apt, makeDate(resizeState.currentStartHour), makeDate(resizeState.currentEndHour));
      setResizeState(null);
    };

    window.addEventListener('mousemove', handleResizeMove);
    window.addEventListener('mouseup', handleResizeUp);
    return () => { window.removeEventListener('mousemove', handleResizeMove); window.removeEventListener('mouseup', handleResizeUp); };
  }, [resizeState, currentDate, onAppointmentResize, coachTimezone]);

  const formatTime = (isoString: string) => {
    return new Date(isoString).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: coachTimezone });
  };

  const isToday = currentDate.toDateString() === new Date().toDateString();
  const dayLabel = currentDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

  return (
    <div className={`day-view ${dragState?.isDragging ? 'dragging' : ''} ${resizeState ? 'resizing' : ''}`}>
      <div className={`day-view-header ${isToday ? 'today' : ''}`}>
        <h3>{dayLabel}</h3>
        {isToday && <span className="today-badge">Today</span>}
      </div>

      {/* All-day events banner (day view) */}
      {dayAllDayEvents.length > 0 && (
        <div className="day-allday-banner">
          <span className="day-allday-label">all-day</span>
          <div className="day-allday-events">
            {dayAllDayEvents.map(apt => (
              <div
                key={apt.id}
                className={`allday-chip ${apt.event_type || 'appointment'}`}
                style={{ '--apt-color': getAptColor(apt, serviceColors), '--apt-border-color': getAptBorderColor(apt, getAptColor(apt, serviceColors), currentUserId), borderLeftColor: 'var(--apt-border-color)' } as React.CSSProperties}
                title={getAptTooltip(apt, serviceNames)}
                onDoubleClick={(e) => onAppointmentClick(apt, e)}
              >
                {apt.title} — {getAptSubtitle(apt)}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="day-grid" ref={dayGridRef}>
        <div className="time-gutter">
          {TIME_SLOTS.map((slot, i) => (
            <div key={i} className={`time-label ${slot.isHalfHour ? 'half-hour' : 'on-hour'}`}>{slot.label}</div>
          ))}
        </div>

        <div className={`day-column single ${isToday ? 'today' : ''}`} ref={columnRef}>
          {TIME_SLOTS.map((slot, i) => (
            <div key={i} className={`time-slot ${slot.isHalfHour ? 'on-hour' : 'half-hour'}`}
              onClick={() => !dragState?.isDragging && onTimeSlotClick(currentDate, slot.fractionalHour)} />
          ))}

          {renderAvailabilityOverlays(currentDate, availabilityWindows, coachTimezone, serviceNames)}

          {/* Blocked time overlays (red) */}
          {renderBlockedTimeOverlays(currentDate, blockedTime, coachTimezone)}
          {renderExternalEventOverlays(currentDate, externalEvents, coachTimezone)}

          {isToday && (() => {
            const localNow = new Date(new Date().toLocaleString('en-US', { timeZone: coachTimezone }));
            const hour = localNow.getHours() + localNow.getMinutes() / 60;
            return (
              <div className="current-time-marker" style={{ top: `${(hour - START_HOUR) * DAY_PX}px` }}>
                <div className="time-marker-dot" /><div className="time-marker-line" />
              </div>
            );
          })()}

          <div className="appointments-container">
            {dayAppointments.map(apt => (
              <div
                key={apt.id}
                className={`day-appointment ${apt.status} ${apt.event_type || 'appointment'} ${getViewerBorderClasses(apt, currentUserId)} ${dragState?.apt?.id === apt.id && dragState.isDragging ? 'dragging' : ''} ${resizeState?.apt?.id === apt.id ? 'resizing' : ''}`}
                style={{ ...getAppointmentStyle(apt), '--apt-color': getAptColor(apt, serviceColors), '--apt-border-color': getAptBorderColor(apt, getAptColor(apt, serviceColors), currentUserId), borderLeftColor: 'var(--apt-border-color)' } as React.CSSProperties}
                title={getAptTooltip(apt, serviceNames)}
                onMouseDown={(e) => handleMouseDown(e, apt)}
                onDoubleClick={(e) => !dragState?.isDragging && !resizeState && onAppointmentClick(apt, e)}
              >
                <div className="resize-handle top" onMouseDown={(e) => handleResizeMouseDown(e, apt, 'top')} />
                <div className="apt-content">
                  <div className="apt-time">{formatTime(apt.start_time)} - {formatTime(apt.end_time)}</div>
                  <div className="apt-title">{apt.title}</div>
                  <div className="apt-client"><User size={12} />{getAptSubtitle(apt)}</div>
                  {apt.notes && <div className="apt-notes">{apt.notes}</div>}
                </div>
                <div className="resize-handle bottom" onMouseDown={(e) => handleResizeMouseDown(e, apt, 'bottom')} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const CoachCalendarView: React.FC<CoachCalendarViewProps> = ({ onBack }) => {
  // State
  const [viewMode, setViewMode] = useState<ViewMode>('month');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [showSchedulingAssistant, setShowSchedulingAssistant] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [selectedClientName, setSelectedClientName] = useState<string | null>(null);
  const [editingAppointment, setEditingAppointment] = useState<Appointment | null>(null);
  const [timelineEditAppointment, setTimelineEditAppointment] = useState<Appointment | null>(null);
  const [timelineEditScope, setTimelineEditScope] = useState<'single' | 'series' | undefined>(undefined);

  // Event type picker state (for "New" dropdown and click/drag creation)
  const [showNewDropdown, setShowNewDropdown] = useState(false);
  const [showEventTypePicker, setShowEventTypePicker] = useState<{ date?: string; hour?: number } | null>(null);

  // Personal event creation/edit modal
  const [showPersonalModal, setShowPersonalModal] = useState(false);
  const [editingPersonalId, setEditingPersonalId] = useState<string | null>(null);
  const [personalForm, setPersonalForm] = useState({
    title: 'Busy',
    date: '',
    endDate: '',
    startHour: 9,
    endHour: 10,
    allDay: false,
    free_busy_status: 'busy' as FreeBusyStatus
  });

  // Meeting creation modal
  const [showMeetingModal, setShowMeetingModal] = useState(false);
  const [meetingForm, setMeetingForm] = useState({
    title: '',
    date: '',
    startHour: 9,
    endHour: 10,
    notes: '',
    attendees: [] as Array<{ id: string; name: string; type: 'staff' | 'client'; attendance: AttendanceType }>
  });
  const [editingMeetingId, setEditingMeetingId] = useState<string | null>(null);
  const [staffList, setStaffList] = useState<Array<{ id: string; first_name: string; last_name: string; email: string; role: string }>>([]);
  const [staffSearch, setStaffSearch] = useState('');
  const [staffLoading, setStaffLoading] = useState(false);

  // Meeting scheduling assistant: busy blocks per participant
  const [meetingBusyBlocks, setMeetingBusyBlocks] = useState<Record<string, Array<{ start_time: string; end_time: string; type: string }>>>({});
  const [meetingEnforcement, setMeetingEnforcement] = useState<Record<string, string>>({});
  const [protectedTimeWarning, setProtectedTimeWarning] = useState<{ names: string[]; enforcement: 'hard' | 'soft' } | null>(null);

  // Meeting timeline drag state
  const [mtDrag, setMtDrag] = useState<{
    type: 'move' | 'resize-left' | 'resize-right';
    startX: number;
    origStartHour: number;
    origEndHour: number;
    barWidth: number;
    barLeft: number;
  } | null>(null);

  // Client picker state (for "New Appointment" without client context)
  const [showClientPicker, setShowClientPicker] = useState(false);
  const [clientPickerList, setClientPickerList] = useState<Array<{ id: string; first_name: string; last_name: string; email: string }>>([]);
  const [clientPickerSearch, setClientPickerSearch] = useState('');
  const [clientPickerLoading, setClientPickerLoading] = useState(false);

  // Coach timezone (from localStorage or default to Eastern)
  const coachTimezone = localStorage.getItem('user_timezone') || 'America/New_York';

  // Current user ID (for viewer-based rendering)
  const currentUserId = (() => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return '';
      return JSON.parse(atob(token.split('.')[1])).userId || '';
    } catch { return ''; }
  })();

  // Availability windows for overlay on week/day views
  const [availabilityWindows, setAvailabilityWindows] = useState<AvailWindow[]>([]);
  const [serviceNames, setServiceNames] = useState<Record<string, string>>({});
  const [serviceColors, setServiceColors] = useState<Record<string, string>>({});

  // Pending bookings for sidebar widget
  const [pendingBookings, setPendingBookings] = useState<Appointment[]>([]);

  // OOO coverage conflicts for sidebar nag
  const [oooConflicts, setOooConflicts] = useState<Array<{
    ooo_event: { id: string; title: string; start_time: string; end_time: string; all_day: boolean };
    uncovered_appointments: Array<{ appointment: { id: string; title: string; start_time: string; end_time: string; client_name: string } }>;
  }>>([]);

  // DNS blocked time (rendered on week/day views as red overlays)
  const [blockedTime, setBlockedTime] = useState<Array<{
    id: string; coach_id: string; start_time: string; end_time: string;
    reason: string; type: string; all_day: boolean;
  }>>([]);


  // External calendar events (busy blocks from Google)
  const [externalEvents, setExternalEvents] = useState<Array<{
    start_time: string; end_time: string; status: string; calendar_name: string; all_day: boolean; summary?: string; provider?: 'google' | 'apple';
  }>>([]);
  const [syncingExternal, setSyncingExternal] = useState(false);

  // Get date range for current view
  const getDateRange = useCallback(() => {
    const start = new Date(currentDate);
    const end = new Date(currentDate);

    if (viewMode === 'month') {
      // Get full month range including overflow weeks
      start.setDate(1);
      start.setDate(start.getDate() - start.getDay());
      end.setMonth(end.getMonth() + 1, 0);
      end.setDate(end.getDate() + (6 - end.getDay()));
    } else if (viewMode === 'week') {
      // Get week range (Sunday to Saturday)
      start.setDate(currentDate.getDate() - currentDate.getDay());
      end.setDate(start.getDate() + 6);
    } else {
      // Day view - just the current day
      // start and end are already set to currentDate
    }

    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);

    return { start, end };
  }, [currentDate, viewMode]);

  // Load appointments
  const loadAppointments = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      const { start, end } = getDateRange();

      const params = new URLSearchParams({
        start_date: start.toISOString(),
        end_date: end.toISOString()
      });

      const response = await fetch(`/api/appointments?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setAppointments(data.appointments || []);
      }
    } catch (error) {
      console.error('Error loading appointments:', error);
    } finally {
      setLoading(false);
    }
  }, [getDateRange]);

  // Load availability windows and service names for calendar overlay
  const loadAvailability = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const payload = token ? JSON.parse(atob(token.split('.')[1])) : null;
      const userId = payload?.userId;
      if (!userId) return;

      const [availRes, svcRes, extRes, blockRes, oooRes] = await Promise.all([
        fetch(`/api/availability/${userId}?active_only=true`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch('/api/services?active_only=true', { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch('/api/availability/external-events', { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`/api/blocked-time/${userId}`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`/api/blocked-time/${userId}/ooo-conflicts`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);

      if (availRes.ok) {
        const data = await availRes.json();
        setAvailabilityWindows(data.windows || []);
      }
      if (svcRes.ok) {
        const data = await svcRes.json();
        const names: Record<string, string> = {};
        const colors: Record<string, string> = {};
        for (const s of data.services || []) {
          names[s.id] = s.name;
          if (s.color) colors[s.id] = s.color;
        }
        setServiceNames(names);
        setServiceColors(colors);
      }
      if (extRes.ok) {
        const data = await extRes.json();
        setExternalEvents(data.events || []);
      }
      if (blockRes.ok) {
        const data = await blockRes.json();
        setBlockedTime(data.blocks || []);
      }
      if (oooRes.ok) {
        const data = await oooRes.json();
        setOooConflicts(data.conflicts || []);
      }
    } catch (error) {
      console.error('Error loading availability:', error);
    }
  }, []);

  // Load pending bookings
  const loadPendingBookings = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch('/api/booking/pending', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPendingBookings(data.bookings || []);
      }
    } catch { /* ignore */ }
  }, []);

  // Load appointments on mount and when view/date changes
  useEffect(() => {
    loadAppointments();
  }, [loadAppointments]);

  // Load availability windows and pending bookings once
  useEffect(() => {
    loadAvailability();
    loadPendingBookings();
  }, [loadAvailability, loadPendingBookings]);

  // ESC key handler for cascade navigation
  useEffect(() => {
    const handleEscKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (e.defaultPrevented) return;

      // Close dropdowns/pickers/modals first
      if (showNewDropdown) { setShowNewDropdown(false); return; }
      if (showEventTypePicker) { setShowEventTypePicker(null); return; }
      if (protectedTimeWarning) { setProtectedTimeWarning(null); return; }
      if (showMeetingModal) { setShowMeetingModal(false); return; }
      if (showPersonalModal) { setShowPersonalModal(false); return; }

      // If scheduling assistant is open, close it
      if (showSchedulingAssistant) {
        e.preventDefault();
        e.stopPropagation();
        setShowSchedulingAssistant(false);
        setSelectedDate(null);
        setSelectedClientId(null);
        setSelectedClientName(null);
        setTimelineEditAppointment(null);
        return;
      }

      // If editing appointment modal is open, close it
      if (editingAppointment) {
        e.preventDefault();
        e.stopPropagation();
        setEditingAppointment(null);
        return;
      }

      // Cascade: day -> week -> month -> exit
      if (viewMode === 'day') {
        e.preventDefault();
        setViewMode('week');
      } else if (viewMode === 'week') {
        e.preventDefault();
        setViewMode('month');
      } else if (onBack) {
        e.preventDefault();
        onBack();
      }
    };

    window.addEventListener('keydown', handleEscKey, true);
    return () => window.removeEventListener('keydown', handleEscKey, true);
  }, [viewMode, showSchedulingAssistant, showNewDropdown, showEventTypePicker, showMeetingModal, showPersonalModal, protectedTimeWarning, editingAppointment, onBack]);

  // Navigation handlers
  const goToPrevious = () => {
    const newDate = new Date(currentDate);
    if (viewMode === 'month') {
      newDate.setMonth(newDate.getMonth() - 1);
    } else if (viewMode === 'week') {
      newDate.setDate(newDate.getDate() - 7);
    } else {
      newDate.setDate(newDate.getDate() - 1);
    }
    setCurrentDate(newDate);
  };

  const goToNext = () => {
    const newDate = new Date(currentDate);
    if (viewMode === 'month') {
      newDate.setMonth(newDate.getMonth() + 1);
    } else if (viewMode === 'week') {
      newDate.setDate(newDate.getDate() + 7);
    } else {
      newDate.setDate(newDate.getDate() + 1);
    }
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Get display title based on view mode
  const getDisplayTitle = () => {
    if (viewMode === 'month') {
      return currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    } else if (viewMode === 'week') {
      const start = new Date(currentDate);
      start.setDate(currentDate.getDate() - currentDate.getDay());
      const end = new Date(start);
      end.setDate(start.getDate() + 6);

      if (start.getMonth() === end.getMonth()) {
        return `${MONTHS_FULL[start.getMonth()]} ${start.getDate()} - ${end.getDate()}, ${start.getFullYear()}`;
      } else {
        return `${MONTHS[start.getMonth()]} ${start.getDate()} - ${MONTHS[end.getMonth()]} ${end.getDate()}, ${end.getFullYear()}`;
      }
    } else {
      return currentDate.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      });
    }
  };

  // Mini calendar handlers
  const handleMiniCalendarClick = (date: Date) => {
    setCurrentDate(date);
    if (viewMode === 'month') {
      setViewMode('week');
    }
  };

  const handleMiniCalendarDoubleClick = (date: Date) => {
    setCurrentDate(date);
    setViewMode('day');
  };

  // Date click handler (from month view)
  const handleDateClick = (date: Date) => {
    setCurrentDate(date);
    setViewMode('week');
  };

  // Navigate to a specific appointment in week view (from month chip click)
  const [scrollToApptTime, setScrollToApptTime] = useState<number | null>(null);

  const handleAppointmentNavigate = (apt: Appointment) => {
    const aptDate = new Date(apt.start_time);
    setCurrentDate(aptDate);
    setViewMode('week');
    // Store the pixel position of the appointment start time
    const localStart = new Date(aptDate.toLocaleString('en-US', { timeZone: coachTimezone }));
    const startHour = localStart.getHours() + localStart.getMinutes() / 60;
    setScrollToApptTime(startHour);
  };

  // Effect: scroll to center appointment in view after switching to week
  useEffect(() => {
    if (scrollToApptTime !== null && viewMode === 'week') {
      const timer = setTimeout(() => {
        const grid = document.querySelector('.week-grid');
        if (grid) {
          const apptPx = (scrollToApptTime - START_HOUR) * PX_PER_HOUR;
          const viewHeight = grid.clientHeight;
          // Center the appointment start time in the middle of the view
          grid.scrollTop = Math.max(0, apptPx - viewHeight / 2);
        }
        setScrollToApptTime(null);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [scrollToApptTime, viewMode]);

  // Appointment click handler
  const handleAppointmentClick = (apt: Appointment, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingAppointment(apt);
  };

  // Time slot click handler (for creating new appointments)
  const handleTimeSlotClick = (date: Date, hour: number) => {
    const dateStr = date.toLocaleDateString('en-CA');
    setShowEventTypePicker({ date: dateStr, hour });
  };

  // Handle event type selection from the picker
  const handleEventTypeSelect = (type: EventType) => {
    const pickerContext = showEventTypePicker;
    setShowEventTypePicker(null);
    setShowNewDropdown(false);

    if (type === 'appointment') {
      // Client appointment flow — show client picker, then scheduling assistant
      if (pickerContext?.date) setSelectedDate(pickerContext.date);
      handleNewAppointment();
    } else if (type === 'meeting') {
      // Meeting flow — open meeting creation modal
      const date = pickerContext?.date || currentDate.toLocaleDateString('en-CA', { timeZone: coachTimezone });
      const hour = pickerContext?.hour ?? 9;
      setMeetingForm({
        title: '',
        date,
        startHour: hour,
        endHour: Math.min(hour + 1, 23),
        notes: '',
        attendees: []
      });
      setStaffSearch('');
      setEditingMeetingId(null);
      setShowMeetingModal(true);
      // Fetch staff list if not loaded
      if (staffList.length === 0) {
        setStaffLoading(true);
        const token = localStorage.getItem('auth_token');
        fetch('/api/staff/list', { headers: { 'Authorization': `Bearer ${token}` } })
          .then(r => r.json())
          .then(data => setStaffList(data.staff || []))
          .catch(() => {})
          .finally(() => setStaffLoading(false));
      }
    } else if (type === 'personal') {
      // Personal block — open creation form
      const date = pickerContext?.date || currentDate.toLocaleDateString('en-CA', { timeZone: coachTimezone });
      const hour = pickerContext?.hour ?? new Date().getHours();
      setPersonalForm({
        title: '',
        date,
        endDate: date,
        startHour: hour,
        endHour: Math.min(hour + 1, 23),
        allDay: false,
        free_busy_status: 'busy'
      });
      setEditingPersonalId(null);
      setShowPersonalModal(true);
    }
  };

  // Meeting timeline drag handlers
  useEffect(() => {
    if (!mtDrag) return;
    const TIMELINE_START = 7;
    const TIMELINE_END = 20;
    const TIMELINE_HOURS = TIMELINE_END - TIMELINE_START;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - mtDrag.startX;
      const deltaHours = (deltaX / mtDrag.barWidth) * TIMELINE_HOURS;
      const snapped = Math.round(deltaHours * 4) / 4; // 15-min snap
      const duration = mtDrag.origEndHour - mtDrag.origStartHour;

      if (mtDrag.type === 'move') {
        let newStart = mtDrag.origStartHour + snapped;
        newStart = Math.max(TIMELINE_START, Math.min(newStart, TIMELINE_END - duration));
        setMeetingForm(prev => ({ ...prev, startHour: newStart, endHour: newStart + duration }));
      } else if (mtDrag.type === 'resize-left') {
        let newStart = mtDrag.origStartHour + snapped;
        newStart = Math.max(TIMELINE_START, Math.min(newStart, mtDrag.origEndHour - 0.25));
        setMeetingForm(prev => ({ ...prev, startHour: newStart }));
      } else if (mtDrag.type === 'resize-right') {
        let newEnd = mtDrag.origEndHour + snapped;
        newEnd = Math.max(mtDrag.origStartHour + 0.25, Math.min(newEnd, TIMELINE_END));
        setMeetingForm(prev => ({ ...prev, endHour: newEnd }));
      }
    };

    const handleMouseUp = () => setMtDrag(null);

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => { window.removeEventListener('mousemove', handleMouseMove); window.removeEventListener('mouseup', handleMouseUp); };
  }, [mtDrag]);

  // Fetch busy blocks for all participants via free/busy endpoint
  useEffect(() => {
    if (!showMeetingModal || !meetingForm.date) return;
    const token = localStorage.getItem('auth_token');
    if (!token) return;

    const participantIds = [currentUserId, ...meetingForm.attendees.map(a => a.id)].filter(Boolean);

    Promise.all(participantIds.map(async (pid) => {
      const res = await fetch(`/api/appointments/free-busy/${pid}?date=${meetingForm.date}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) return { pid, blocks: [], enforcement: 'hard' };
      const data = await res.json();
      return { pid, blocks: data.busy || [], enforcement: data.block_enforcement || 'hard' };
    })).then(results => {
      const blockMap: Record<string, Array<{ start_time: string; end_time: string; type: string }>> = {};
      const enfMap: Record<string, string> = {};
      for (const r of results) {
        blockMap[r.pid] = r.blocks;
        enfMap[r.pid] = r.enforcement;
      }
      setMeetingBusyBlocks(blockMap);
      setMeetingEnforcement(enfMap);
    });
  }, [showMeetingModal, meetingForm.date, meetingForm.attendees, currentUserId]);

  // Compute per-attendee conflict status reactively (for real-time UI feedback)
  const attendeeConflicts = (() => {
    if (!meetingForm.date) return {};
    const startTime = new Date(`${meetingForm.date}T00:00:00`);
    startTime.setHours(Math.floor(meetingForm.startHour), Math.round((meetingForm.startHour % 1) * 60));
    const endTime = new Date(`${meetingForm.date}T00:00:00`);
    endTime.setHours(Math.floor(meetingForm.endHour), Math.round((meetingForm.endHour % 1) * 60));
    const start = startTime.getTime();
    const end = endTime.getTime();

    const conflicts: Record<string, 'hard' | 'soft'> = {};
    for (const a of meetingForm.attendees) {
      const blocks = (meetingBusyBlocks[a.id] || []).filter(b => b.type === 'blocked');
      const enforcement = (meetingEnforcement[a.id] || 'hard') as 'hard' | 'soft';
      const hasConflict = blocks.some(b => {
        const bStart = new Date(b.start_time).getTime();
        const bEnd = new Date(b.end_time).getTime();
        return start < bEnd && end > bStart;
      });
      if (hasConflict) conflicts[a.id] = enforcement;
    }
    return conflicts;
  })();

  const hasHardConflict = Object.values(attendeeConflicts).includes('hard');

  // Check for protected time conflicts before creating
  const checkProtectedTimeConflicts = (): { names: string[]; enforcement: 'hard' | 'soft' } | null => {
    const startTime = new Date(`${meetingForm.date}T00:00:00`);
    startTime.setHours(Math.floor(meetingForm.startHour), Math.round((meetingForm.startHour % 1) * 60));
    const endTime = new Date(`${meetingForm.date}T00:00:00`);
    endTime.setHours(Math.floor(meetingForm.endHour), Math.round((meetingForm.endHour % 1) * 60));
    const start = startTime.getTime();
    const end = endTime.getTime();

    const hardBlocked: string[] = [];
    const softBlocked: string[] = [];

    // Only check attendees — organizer can override their own protected time
    for (const p of meetingForm.attendees) {
      const blocks = (meetingBusyBlocks[p.id] || []).filter(b => b.type === 'blocked');
      const enforcement = meetingEnforcement[p.id] || 'hard';
      const hasConflict = blocks.some(b => {
        const bStart = new Date(b.start_time).getTime();
        const bEnd = new Date(b.end_time).getTime();
        return start < bEnd && end > bStart;
      });
      if (hasConflict) {
        if (enforcement === 'hard') hardBlocked.push(p.name);
        else softBlocked.push(p.name);
      }
    }

    if (hardBlocked.length > 0) return { names: hardBlocked, enforcement: 'hard' };
    if (softBlocked.length > 0) return { names: softBlocked, enforcement: 'soft' };
    return null;
  };

  // Attempt to create meeting — checks protected time first
  const attemptSaveMeeting = () => {
    const conflict = checkProtectedTimeConflicts();
    if (conflict) {
      setProtectedTimeWarning(conflict);
    } else {
      saveMeeting();
    }
  };

  // Create a meeting from the modal form
  const saveMeeting = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const payload = token ? JSON.parse(atob(token.split('.')[1])) : null;
      const userId = payload?.userId;
      if (!userId) return;

      const startTime = new Date(`${meetingForm.date}T00:00:00`);
      startTime.setHours(Math.floor(meetingForm.startHour), Math.round((meetingForm.startHour % 1) * 60));
      const endTime = new Date(`${meetingForm.date}T00:00:00`);
      endTime.setHours(Math.floor(meetingForm.endHour), Math.round((meetingForm.endHour % 1) * 60));

      if (editingMeetingId) {
        // Update existing meeting
        const response = await fetch(`/api/appointments/${editingMeetingId}`, {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            title: meetingForm.title || 'Meeting',
            notes: meetingForm.notes,
            start_time: startTime.toISOString(),
            end_time: endTime.toISOString(),
            attendees: meetingForm.attendees.map(a => ({ id: a.id, type: a.type, name: a.name, status: 'pending', attendance: a.attendance || 'required' }))
          })
        });
        if (response.ok) {
          setShowMeetingModal(false);
          setEditingMeetingId(null);
          loadAppointments();
        } else {
          const data = await response.json().catch(() => ({}));
          alert(data.error || 'Failed to update meeting');
        }
      } else {
        // Create new meeting
        const response = await fetch('/api/appointments', {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            organizer: { id: userId, type: 'staff', name: '' },
            attendees: meetingForm.attendees.map(a => ({ id: a.id, type: a.type, name: a.name, status: 'pending', attendance: a.attendance || 'required' })),
            title: meetingForm.title || 'Meeting',
            notes: meetingForm.notes,
            start_time: startTime.toISOString(),
            end_time: endTime.toISOString(),
            event_type: 'meeting',
            free_busy_status: 'busy'
          })
        });

        if (response.ok) {
          setShowMeetingModal(false);
          loadAppointments();
        } else {
          const data = await response.json().catch(() => ({}));
          if (data.code === 'PROTECTED_TIME') {
            setProtectedTimeWarning({ names: [data.error], enforcement: 'hard' });
          } else {
            alert(data.error || 'Failed to create meeting');
          }
        }
      }
    } catch (error) {
      console.error('Error creating/updating meeting:', error);
    }
  };

  // Create a personal event (OOO/busy/focus) from the modal form
  const savePersonalEvent = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const payload = token ? JSON.parse(atob(token.split('.')[1])) : null;
      const userId = payload?.userId;
      if (!userId) return;

      let startTime: Date, endTime: Date;
      if (personalForm.allDay) {
        startTime = new Date(`${personalForm.date}T00:00:00`);
        endTime = new Date(`${personalForm.endDate || personalForm.date}T23:59:59`);
      } else {
        startTime = new Date(`${personalForm.date}T00:00:00`);
        startTime.setHours(Math.floor(personalForm.startHour), Math.round((personalForm.startHour % 1) * 60));
        endTime = new Date(`${personalForm.date}T00:00:00`);
        endTime.setHours(Math.floor(personalForm.endHour), Math.round((personalForm.endHour % 1) * 60));
      }

      const title = personalForm.title || { busy: 'Busy', out_of_office: 'Out of Office', tentative: 'Tentative', free: 'Free' }[personalForm.free_busy_status] || 'Busy';

      if (editingPersonalId) {
        const response = await fetch(`/api/appointments/${editingPersonalId}`, {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            title,
            start_time: startTime.toISOString(),
            end_time: endTime.toISOString(),
            free_busy_status: personalForm.free_busy_status,
            all_day: personalForm.allDay
          })
        });
        if (response.ok) {
          setShowPersonalModal(false);
          setEditingPersonalId(null);
          loadAppointments();
        }
      } else {
        const response = await fetch('/api/appointments', {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            organizer: { id: userId, type: 'staff', name: '' },
            attendees: [],
            title,
            start_time: startTime.toISOString(),
            end_time: endTime.toISOString(),
            event_type: 'personal',
            free_busy_status: personalForm.free_busy_status,
            all_day: personalForm.allDay
          })
        });
        if (response.ok) {
          setShowPersonalModal(false);
          loadAppointments();
        }
      }
    } catch (error) {
      console.error('Error creating personal event:', error);
    }
  };

  // Handle appointment move (drag)
  const handleAppointmentMove = async (apt: Appointment, newStart: Date) => {
    try {
      const token = localStorage.getItem('auth_token');

      // Calculate new end time based on duration
      const newEnd = new Date(newStart.getTime() + apt.duration_minutes * 60 * 1000);

      const response = await fetch(`/api/appointments/${apt.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          start_time: newStart.toISOString(),
          end_time: newEnd.toISOString()
        })
      });

      if (response.ok) {
        loadAppointments();
      } else {
        alert('Failed to move appointment');
        loadAppointments(); // Refresh to snap back
      }
    } catch (error) {
      console.error('Error moving appointment:', error);
    }
  };

  // Handle appointment resize (drag top/bottom edge)
  const handleAppointmentResize = async (apt: Appointment, newStart: Date, newEnd: Date) => {
    try {
      const token = localStorage.getItem('auth_token');

      const response = await fetch(`/api/appointments/${apt.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          start_time: newStart.toISOString(),
          end_time: newEnd.toISOString()
        })
      });

      if (response.ok) {
        loadAppointments();
      } else {
        console.error('Failed to resize appointment');
      }
    } catch (error) {
      console.error('Error resizing appointment:', error);
    }
  };

  // New appointment handler — show client picker first
  const handleNewAppointment = async () => {
    setSelectedDate(null);
    setShowClientPicker(true);
    setClientPickerSearch('');

    // Fetch client list if not already loaded
    if (clientPickerList.length === 0) {
      setClientPickerLoading(true);
      try {
        const token = localStorage.getItem('auth_token');
        const response = await fetch('/api/coach/clients', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setClientPickerList(data.clients || []);
        }
      } catch (err) {
        console.error('Error loading clients for picker:', err);
      } finally {
        setClientPickerLoading(false);
      }
    }
  };

  // Client selected from picker — open scheduling assistant
  const handleClientSelected = (client: { id: string; first_name: string; last_name: string }) => {
    setSelectedClientId(client.id);
    setSelectedClientName(`${client.first_name} ${client.last_name}`.trim());
    setShowClientPicker(false);
    setShowSchedulingAssistant(true);
  };

  // Appointment created/updated handler
  const handleAppointmentCreated = () => {
    setShowSchedulingAssistant(false);
    setSelectedDate(null);
    setSelectedClientId(null);
    setSelectedClientName(null);
    setTimelineEditAppointment(null);
    setTimelineEditScope(undefined);
    loadAppointments();
  };

  // Appointment updated handler
  const handleAppointmentUpdated = () => {
    setEditingAppointment(null);
    loadAppointments();
  };

  // Appointment deleted handler
  const handleAppointmentDeleted = () => {
    setEditingAppointment(null);
    loadAppointments();
  };

  // Handle "Edit" click from modal — route to the appropriate editor based on event type
  const handleEditInTimeline = (scope: 'single' | 'series' = 'single') => {
    if (!editingAppointment) return;

    if (editingAppointment.event_type === 'meeting') {
      // Open meeting modal pre-populated with existing data
      const apt = editingAppointment;
      const startLocal = new Date(new Date(apt.start_time).toLocaleString('en-US', { timeZone: coachTimezone }));
      const endLocal = new Date(new Date(apt.end_time).toLocaleString('en-US', { timeZone: coachTimezone }));
      setMeetingForm({
        title: apt.title,
        date: new Date(apt.start_time).toLocaleDateString('en-CA', { timeZone: coachTimezone }),
        startHour: startLocal.getHours() + startLocal.getMinutes() / 60,
        endHour: endLocal.getHours() + endLocal.getMinutes() / 60,
        notes: apt.notes || '',
        attendees: (apt.attendees || []).filter(a => a.type === 'staff').map(a => ({ id: a.id, name: a.name, type: 'staff' as const, attendance: (a.attendance || 'required') as AttendanceType }))
      });
      setStaffSearch('');
      setEditingMeetingId(apt.id);
      setEditingAppointment(null);
      setShowMeetingModal(true);
      // Fetch staff list if needed
      if (staffList.length === 0) {
        const token = localStorage.getItem('auth_token');
        fetch('/api/staff/list', { headers: { 'Authorization': `Bearer ${token}` } })
          .then(r => r.json())
          .then(data => setStaffList(data.staff || []))
          .catch(() => {});
      }
    } else if (editingAppointment.event_type === 'personal') {
      // Open personal modal pre-populated
      const apt = editingAppointment;
      const startLocal = new Date(new Date(apt.start_time).toLocaleString('en-US', { timeZone: coachTimezone }));
      const endLocal = new Date(new Date(apt.end_time).toLocaleString('en-US', { timeZone: coachTimezone }));
      setPersonalForm({
        title: apt.title,
        date: new Date(apt.start_time).toLocaleDateString('en-CA', { timeZone: coachTimezone }),
        endDate: new Date(apt.end_time).toLocaleDateString('en-CA', { timeZone: coachTimezone }),
        startHour: startLocal.getHours() + startLocal.getMinutes() / 60,
        endHour: endLocal.getHours() + endLocal.getMinutes() / 60,
        allDay: apt.all_day || false,
        free_busy_status: (apt.free_busy_status || 'busy') as FreeBusyStatus
      });
      setEditingPersonalId(apt.id);
      setEditingAppointment(null);
      setShowPersonalModal(true);
    } else {
      // Client appointment — use the scheduling assistant
      const clientId = getClientId(editingAppointment);
      const clientName = getClientName(editingAppointment);
      setSelectedClientId(clientId);
      setSelectedClientName(clientName);
      setTimelineEditAppointment(editingAppointment);
      setTimelineEditScope(scope);
      setEditingAppointment(null);
      setShowSchedulingAssistant(true);
    }
  };

  return (
    <div className="coach-calendar-view">
      {/* Sidebar with Mini Calendar */}
      <div className="calendar-sidebar">
        <div className="new-event-dropdown-wrapper">
          <button className="btn-new-appointment" onClick={() => setShowNewDropdown(!showNewDropdown)}>
            <Plus size={16} />
            New
          </button>
          {showNewDropdown && (
            <div className="new-event-dropdown">
              <button className="new-event-option appointment" onClick={() => handleEventTypeSelect('appointment')}>
                <User size={14} />
                <div>
                  <div className="option-title">Appointment</div>
                  <div className="option-desc">Client session</div>
                </div>
              </button>
              <button className="new-event-option meeting" onClick={() => handleEventTypeSelect('meeting')}>
                <Layers size={14} />
                <div>
                  <div className="option-title">Meeting</div>
                  <div className="option-desc">Staff / internal</div>
                </div>
              </button>
              <button className="new-event-option personal" onClick={() => handleEventTypeSelect('personal')}>
                <Clock size={14} />
                <div>
                  <div className="option-title">Personal</div>
                  <div className="option-desc">OOO, busy, focus</div>
                </div>
              </button>
            </div>
          )}
        </div>

        <MiniCalendarSidebar
          currentDate={currentDate}
          selectedDate={currentDate}
          viewMode={viewMode}
          onDateClick={handleMiniCalendarClick}
          onDateDoubleClick={handleMiniCalendarDoubleClick}
          onMonthChange={setCurrentDate}
        />

        <div className="timezone-info">
          <Clock size={14} />
          <span>{coachTimezone.replace('_', ' ')}</span>
        </div>

        {/* Pending Bookings Widget */}
        {pendingBookings.length > 0 && (
          <div className="pending-bookings-widget">
            <div className="pending-header">
              <span className="pending-badge">{pendingBookings.length}</span>
              Pending Booking{pendingBookings.length !== 1 ? 's' : ''}
            </div>
            <div className="pending-list">
              {pendingBookings.map(b => (
                <div key={b.id} className="pending-item" onClick={() => handleAppointmentClick(b, { stopPropagation: () => {}, preventDefault: () => {} } as React.MouseEvent)}>
                  <div className="pending-name">{b.booking?.requested_by_name || 'Unknown'}</div>
                  <div className="pending-detail">
                    {b.title} — {new Date(b.start_time).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} at {new Date(b.start_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: coachTimezone })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* OOO Coverage Nag Widget */}
        {oooConflicts.length > 0 && (
          <div className="ooo-nag-widget">
            <div className="ooo-nag-header">
              <span className="ooo-nag-icon">&#9888;</span>
              OOO Coverage Needed
            </div>
            {oooConflicts.map(conflict => {
              const startDate = new Date(conflict.ooo_event.start_time).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: coachTimezone });
              const endDate = new Date(conflict.ooo_event.end_time).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: coachTimezone });
              const dateRange = conflict.ooo_event.all_day && startDate !== endDate ? `${startDate} - ${endDate}` : startDate;
              return (
                <div key={conflict.ooo_event.id} className="ooo-nag-block">
                  <div className="ooo-nag-dates">
                    {conflict.ooo_event.title}: {dateRange}
                  </div>
                  <div className="ooo-nag-count">
                    {conflict.uncovered_appointments.length} client appointment{conflict.uncovered_appointments.length !== 1 ? 's' : ''} without backup coverage
                  </div>
                  <div className="ooo-nag-appts">
                    {conflict.uncovered_appointments.slice(0, 3).map(item => (
                      <div key={item.appointment.id} className="ooo-nag-appt">
                        <span className="ooo-nag-appt-time">
                          {new Date(item.appointment.start_time).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: coachTimezone })} at {new Date(item.appointment.start_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: coachTimezone })}
                        </span>
                        <span className="ooo-nag-appt-title">{item.appointment.title} — {item.appointment.client_name}</span>
                      </div>
                    ))}
                    {conflict.uncovered_appointments.length > 3 && (
                      <div className="ooo-nag-more">and {conflict.uncovered_appointments.length - 3} more...</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Pending Meetings Nag Widget */}
        {(() => {
          const pendingMeetings = appointments.filter(a =>
            a.event_type === 'meeting' &&
            a.status !== 'cancelled' &&
            (a.attendees || []).some(att => att.id === currentUserId && att.status === 'pending')
          );
          if (pendingMeetings.length === 0) return null;
          return (
            <div className="pending-meetings-widget">
              <div className="pending-meetings-header">
                <span className="pending-meetings-badge">{pendingMeetings.length}</span>
                Meeting{pendingMeetings.length !== 1 ? 's' : ''} Awaiting Response
              </div>
              <div className="pending-meetings-list">
                {pendingMeetings.map(m => (
                  <div key={m.id} className="pending-meeting-item" onClick={() => handleAppointmentClick(m, { stopPropagation: () => {}, preventDefault: () => {} } as React.MouseEvent)}>
                    <div className="pending-meeting-title">{m.title}</div>
                    <div className="pending-meeting-detail">
                      {new Date(m.start_time).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: coachTimezone })} at {new Date(m.start_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: coachTimezone })}
                      {' — '}{m.organizer?.name || 'Organizer'}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

      </div>

      {/* Main Calendar Area */}
      <div className="calendar-main">
        {/* Header */}
        <div className="calendar-header">
          <div className="header-left">
            <div className="nav-buttons">
              <button className="nav-btn" onClick={goToPrevious}>
                <ChevronLeft size={18} />
              </button>
              <button className="nav-btn today-btn" onClick={goToToday}>
                Today
              </button>
              <button className="nav-btn" onClick={goToNext}>
                <ChevronRight size={18} />
              </button>
            </div>
            <h2 className="calendar-title">{getDisplayTitle()}</h2>
          </div>

          <div className="header-right">
            {/* Sync Now button — fans out to all connected providers */}
            <button
              className="nav-btn"
              onClick={async () => {
                setSyncingExternal(true);
                try {
                  const token = localStorage.getItem('auth_token');
                  // Fire both syncs in parallel; non-connected providers return 404 silently
                  await Promise.allSettled([
                    fetch('/api/integrations/calendar/sync', {
                      method: 'POST',
                      headers: { 'Authorization': `Bearer ${token}` }
                    }),
                    fetch('/api/integrations/apple-calendar/sync', {
                      method: 'POST',
                      headers: { 'Authorization': `Bearer ${token}` }
                    })
                  ]);
                  // Reload calendar data to show fresh events
                  loadAppointments();
                  loadAvailability();
                } finally {
                  setSyncingExternal(false);
                }
              }}
              disabled={syncingExternal}
              title="Sync external calendars now (Google + Apple)"
              style={{ marginRight: '8px' }}
            >
              <RefreshCw size={16} className={syncingExternal ? 'spinning' : ''} />
            </button>

            {/* View Mode Toggle */}
            <div className="view-toggle">
              <button
                className={`toggle-btn ${viewMode === 'month' ? 'active' : ''}`}
                onClick={() => setViewMode('month')}
                title="Month view"
              >
                <Grid3X3 size={16} />
                Month
              </button>
              <button
                className={`toggle-btn ${viewMode === 'week' ? 'active' : ''}`}
                onClick={() => setViewMode('week')}
                title="Week view"
              >
                <Layers size={16} />
                Week
              </button>
              <button
                className={`toggle-btn ${viewMode === 'day' ? 'active' : ''}`}
                onClick={() => setViewMode('day')}
                title="Day view"
              >
                <List size={16} />
                Day
              </button>
            </div>
          </div>
        </div>

        {/* Calendar Content */}
        <div className="calendar-content">
          {loading && (
            <div className="loading-overlay">
              <div className="loading-spinner" />
            </div>
          )}

          {viewMode === 'month' && (
            <MonthView
              currentDate={currentDate}
              appointments={appointments}
              coachTimezone={coachTimezone}
              externalEvents={externalEvents}
              serviceColors={serviceColors}
              serviceNames={serviceNames}
              currentUserId={currentUserId}
              onDateClick={handleDateClick}
              onAppointmentClick={handleAppointmentClick}
              onAppointmentNavigate={handleAppointmentNavigate}
            />
          )}

          {viewMode === 'week' && (
            <WeekView
              currentDate={currentDate}
              appointments={appointments}
              coachTimezone={coachTimezone}
              availabilityWindows={availabilityWindows}
              externalEvents={externalEvents}
              serviceNames={serviceNames}
              serviceColors={serviceColors}
              blockedTime={blockedTime}
              currentUserId={currentUserId}
              onAppointmentClick={handleAppointmentClick}
              onTimeSlotClick={handleTimeSlotClick}
              onAppointmentMove={handleAppointmentMove}
              onAppointmentResize={handleAppointmentResize}
            />
          )}

          {viewMode === 'day' && (
            <DayView
              currentDate={currentDate}
              appointments={appointments}
              coachTimezone={coachTimezone}
              availabilityWindows={availabilityWindows}
              externalEvents={externalEvents}
              blockedTime={blockedTime}
              serviceNames={serviceNames}
              serviceColors={serviceColors}
              currentUserId={currentUserId}
              onAppointmentClick={handleAppointmentClick}
              onTimeSlotClick={handleTimeSlotClick}
              onAppointmentMove={handleAppointmentMove}
              onAppointmentResize={handleAppointmentResize}
            />
          )}
        </div>
      </div>

      {/* Personal Event Creation Modal */}
      {showPersonalModal && (
        <div className="event-type-picker-overlay" onMouseDown={e => { if (e.target === e.currentTarget) setShowPersonalModal(false); }}>
          <div className="personal-event-modal">
            <h3>{editingPersonalId ? 'Edit Personal Event' : 'Personal Event'}</h3>
            <div className="personal-form">
              <div className="form-group">
                <label>Title</label>
                <input
                  type="text"
                  value={personalForm.title}
                  onChange={e => setPersonalForm(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g., Lunch, Focus time, Doctor"
                />
              </div>
              <div className="form-group">
                <label>Show as</label>
                <select
                  value={personalForm.free_busy_status}
                  onChange={e => setPersonalForm(prev => ({ ...prev, free_busy_status: e.target.value as FreeBusyStatus }))}
                >
                  <option value="busy">Busy</option>
                  <option value="out_of_office">Out of Office</option>
                  <option value="tentative">Tentative</option>
                  <option value="free">Free</option>
                </select>
              </div>
              <label className="personal-allday-toggle">
                <input
                  type="checkbox"
                  checked={personalForm.allDay}
                  onChange={e => setPersonalForm(prev => ({ ...prev, allDay: e.target.checked }))}
                />
                All Day
              </label>

              {personalForm.allDay ? (
                <div className="form-row-2">
                  <div className="form-group">
                    <label>Start Date</label>
                    <input type="date" value={personalForm.date} onChange={e => setPersonalForm(prev => ({ ...prev, date: e.target.value, endDate: prev.endDate < e.target.value ? e.target.value : prev.endDate }))} />
                  </div>
                  <div className="form-group">
                    <label>End Date</label>
                    <input type="date" value={personalForm.endDate} min={personalForm.date} onChange={e => setPersonalForm(prev => ({ ...prev, endDate: e.target.value }))} />
                  </div>
                </div>
              ) : (
                <>
                  <div className="form-group">
                    <label>Date</label>
                    <input type="date" value={personalForm.date} onChange={e => setPersonalForm(prev => ({ ...prev, date: e.target.value }))} />
                  </div>
                  <div className="form-row-2">
                    <div className="form-group">
                      <label>Start</label>
                      <select value={personalForm.startHour} onChange={e => setPersonalForm(prev => ({ ...prev, startHour: parseFloat(e.target.value) }))}>
                        {Array.from({ length: 24 * 4 }, (_, i) => {
                          const h = i * 0.25;
                          const hh = Math.floor(h);
                          const mm = Math.round((h % 1) * 60);
                          const ampm = hh >= 12 ? 'PM' : 'AM';
                          const h12 = hh === 0 ? 12 : hh > 12 ? hh - 12 : hh;
                          const label = mm === 0 ? `${h12}:00 ${ampm}` : `${h12}:${String(mm).padStart(2, '0')} ${ampm}`;
                          return <option key={h} value={h}>{label}</option>;
                        })}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>End</label>
                      <select value={personalForm.endHour} onChange={e => setPersonalForm(prev => ({ ...prev, endHour: parseFloat(e.target.value) }))}>
                        {Array.from({ length: 24 * 4 }, (_, i) => {
                          const h = i * 0.25;
                          if (h <= personalForm.startHour) return null;
                          const hh = Math.floor(h);
                          const mm = Math.round((h % 1) * 60);
                          const ampm = hh >= 12 ? 'PM' : 'AM';
                          const h12 = hh === 0 ? 12 : hh > 12 ? hh - 12 : hh;
                          const label = mm === 0 ? `${h12}:00 ${ampm}` : `${h12}:${String(mm).padStart(2, '0')} ${ampm}`;
                          return <option key={h} value={h}>{label}</option>;
                        })}
                      </select>
                    </div>
                  </div>
                </>
              )}
            </div>
            <div className="personal-form-footer">
              <button className="btn btn-secondary" onClick={() => setShowPersonalModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={savePersonalEvent} disabled={!personalForm.date}>{editingPersonalId ? 'Update' : 'Create'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Meeting Creation Modal */}
      {showMeetingModal && (
        <div className="event-type-picker-overlay" onMouseDown={e => { if (e.target === e.currentTarget) setShowMeetingModal(false); }}>
          <div className="meeting-modal">
            <h3>{editingMeetingId ? 'Edit Meeting' : 'New Meeting'}</h3>
            <div className="meeting-form">
              <div className="form-group">
                <label>Title</label>
                <input
                  type="text"
                  value={meetingForm.title}
                  onChange={e => setMeetingForm(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g., Team Standup, 1:1 with Court"
                  autoFocus
                />
              </div>

              <div className="form-row-3">
                <div className="form-group">
                  <label>Date</label>
                  <input type="date" value={meetingForm.date} onChange={e => setMeetingForm(prev => ({ ...prev, date: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>Start</label>
                  <select value={meetingForm.startHour} onChange={e => setMeetingForm(prev => ({ ...prev, startHour: parseFloat(e.target.value) }))}>
                    {Array.from({ length: 24 * 4 }, (_, i) => {
                      const h = i * 0.25;
                      const hh = Math.floor(h);
                      const mm = Math.round((h % 1) * 60);
                      const ampm = hh >= 12 ? 'PM' : 'AM';
                      const h12 = hh === 0 ? 12 : hh > 12 ? hh - 12 : hh;
                      const label = mm === 0 ? `${h12}:00 ${ampm}` : `${h12}:${String(mm).padStart(2, '0')} ${ampm}`;
                      return <option key={h} value={h}>{label}</option>;
                    })}
                  </select>
                </div>
                <div className="form-group">
                  <label>End</label>
                  <select value={meetingForm.endHour} onChange={e => setMeetingForm(prev => ({ ...prev, endHour: parseFloat(e.target.value) }))}>
                    {Array.from({ length: 24 * 4 }, (_, i) => {
                      const h = i * 0.25;
                      if (h <= meetingForm.startHour) return null;
                      const hh = Math.floor(h);
                      const mm = Math.round((h % 1) * 60);
                      const ampm = hh >= 12 ? 'PM' : 'AM';
                      const h12 = hh === 0 ? 12 : hh > 12 ? hh - 12 : hh;
                      const label = mm === 0 ? `${h12}:00 ${ampm}` : `${h12}:${String(mm).padStart(2, '0')} ${ampm}`;
                      return <option key={h} value={h}>{label}</option>;
                    })}
                  </select>
                </div>
              </div>

              {/* Scheduling Timeline */}
              {meetingForm.date && (() => {
                const TIMELINE_START = 7; // 7 AM
                const TIMELINE_END = 20; // 8 PM
                const TIMELINE_HOURS = TIMELINE_END - TIMELINE_START;
                const hourToPercent = (h: number) => ((h - TIMELINE_START) / TIMELINE_HOURS) * 100;
                const isoToHour = (iso: string) => {
                  const d = new Date(iso);
                  const local = new Date(d.toLocaleString('en-US', { timeZone: coachTimezone }));
                  return local.getHours() + local.getMinutes() / 60;
                };

                const participants = [
                  { id: currentUserId, name: 'You (Organizer)', isOrganizer: true },
                  ...meetingForm.attendees.map(a => ({ id: a.id, name: a.name, isOrganizer: false }))
                ];

                const proposedLeft = Math.max(0, hourToPercent(meetingForm.startHour));
                const proposedWidth = Math.max(2, hourToPercent(meetingForm.endHour) - proposedLeft);

                const hours = Array.from({ length: TIMELINE_HOURS + 1 }, (_, i) => {
                  const h = TIMELINE_START + i;
                  return { h, pct: hourToPercent(h), label: h === 0 ? '12a' : h === 12 ? '12p' : h > 12 ? `${h-12}p` : `${h}a` };
                });

                return (
                  <div className="meeting-timeline">
                    <label>Scheduling Assistant</label>
                    <div className="mt-timeline-container" style={{ position: 'relative' }}>
                      {/* Hour labels */}
                      <div className="mt-hours">
                        <div className="mt-label-spacer" />
                        <div className="mt-hours-bar">
                          {hours.map(h => (
                            <span key={h.h} className="mt-hour-mark" style={{ left: `${h.pct}%` }}>{h.label}</span>
                          ))}
                        </div>
                      </div>

                      {/* Participant rows */}
                      {participants.map(p => {
                        const blocks = meetingBusyBlocks[p.id] || [];
                        const conflict = !p.isOrganizer ? attendeeConflicts[p.id] : undefined;
                        return (
                          <div key={p.id} className={`mt-participant-row ${conflict ? `conflict-${conflict}` : ''}`}>
                            <div className="mt-participant-label">
                              {!p.isOrganizer && (
                                <button
                                  className={`mt-attendance-toggle ${meetingForm.attendees.find(a => a.id === p.id)?.attendance || 'required'}`}
                                  title={`Click to toggle: ${meetingForm.attendees.find(a => a.id === p.id)?.attendance === 'optional' ? 'optional → required' : 'required → optional'}`}
                                  onClick={() => setMeetingForm(prev => ({
                                    ...prev,
                                    attendees: prev.attendees.map(a => a.id === p.id ? { ...a, attendance: a.attendance === 'optional' ? 'required' as AttendanceType : 'optional' as AttendanceType } : a)
                                  }))}
                                >
                                  {meetingForm.attendees.find(a => a.id === p.id)?.attendance === 'optional' ? 'Opt' : 'Req'}
                                </button>
                              )}
                              <span className={`mt-name ${conflict ? `mt-conflict-${conflict}` : ''}`}>{p.name}</span>
                              {!p.isOrganizer && (
                                <button className="mt-remove" onClick={() => setMeetingForm(prev => ({ ...prev, attendees: prev.attendees.filter(a => a.id !== p.id) }))}>
                                  <X size={10} />
                                </button>
                              )}
                            </div>
                            <div className="mt-participant-bar">
                              {hours.map(h => (
                                <div key={h.h} className="mt-bar-divider" style={{ left: `${h.pct}%` }} />
                              ))}
                              {blocks.map((b, bi) => {
                                const bStart = isoToHour(b.start_time);
                                const bEnd = isoToHour(b.end_time);
                                const left = Math.max(0, hourToPercent(bStart));
                                const width = Math.max(1, hourToPercent(bEnd) - left);
                                return (
                                  <div key={bi} className={`mt-busy-block ${b.type === 'blocked' ? 'mt-blocked' : ''}`} style={{ left: `${left}%`, width: `${width}%` }} title={b.type === 'blocked' ? 'Protected Time' : 'Busy'} />
                                );
                              })}
                              <div
                                className={`mt-proposed ${conflict === 'hard' ? 'mt-proposed-blocked' : conflict === 'soft' ? 'mt-proposed-warn' : ''} ${mtDrag ? 'dragging' : ''}`}
                                style={{ left: `${proposedLeft}%`, width: `${proposedWidth}%` }}
                                onMouseDown={(e) => {
                                  if (e.button !== 0) return;
                                  e.stopPropagation();
                                  const bar = e.currentTarget.parentElement;
                                  if (!bar) return;
                                  const rect = bar.getBoundingClientRect();
                                  setMtDrag({ type: 'move', startX: e.clientX, origStartHour: meetingForm.startHour, origEndHour: meetingForm.endHour, barWidth: rect.width, barLeft: rect.left });
                                }}
                              >
                                <div className="mt-resize-handle mt-resize-left" onMouseDown={(e) => {
                                  e.stopPropagation();
                                  const bar = e.currentTarget.parentElement?.parentElement;
                                  if (!bar) return;
                                  const rect = bar.getBoundingClientRect();
                                  setMtDrag({ type: 'resize-left', startX: e.clientX, origStartHour: meetingForm.startHour, origEndHour: meetingForm.endHour, barWidth: rect.width, barLeft: rect.left });
                                }} />
                                <div className="mt-resize-handle mt-resize-right" onMouseDown={(e) => {
                                  e.stopPropagation();
                                  const bar = e.currentTarget.parentElement?.parentElement;
                                  if (!bar) return;
                                  const rect = bar.getBoundingClientRect();
                                  setMtDrag({ type: 'resize-right', startX: e.clientX, origStartHour: meetingForm.startHour, origEndHour: meetingForm.endHour, barWidth: rect.width, barLeft: rect.left });
                                }} />
                              </div>
                            </div>
                          </div>
                        );
                      })}

                      {/* Add attendee row — inline picker */}
                      <div className="mt-participant-row mt-add-row">
                        <div className="mt-participant-label">
                          <div className="mt-inline-search">
                            <input
                              type="text"
                              value={staffSearch}
                              onChange={e => setStaffSearch(e.target.value)}
                              placeholder="Add attendee..."
                              className="mt-search-input"
                            />
                            {staffSearch.length > 0 && (
                              <div className="staff-autocomplete">
                                {staffLoading ? (
                                  <div className="autocomplete-loading">Loading...</div>
                                ) : (() => {
                                  const filtered = staffList.filter(s =>
                                    s.id !== currentUserId &&
                                    !meetingForm.attendees.some(a => a.id === s.id) &&
                                    `${s.first_name} ${s.last_name} ${s.email}`.toLowerCase().includes(staffSearch.toLowerCase())
                                  );
                                  return filtered.length > 0 ? filtered.map(s => (
                                    <button key={s.id} type="button" className="autocomplete-option" onClick={() => {
                                      setMeetingForm(prev => ({ ...prev, attendees: [...prev.attendees, { id: s.id, name: `${s.first_name} ${s.last_name}`, type: 'staff', attendance: 'required' as AttendanceType }] }));
                                      setStaffSearch('');
                                    }}>
                                      <span className="ac-name">{s.first_name} {s.last_name}</span>
                                      <span className="ac-email">{s.email}</span>
                                    </button>
                                  )) : <div className="autocomplete-empty">No matching staff</div>;
                                })()}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="mt-participant-bar mt-empty-bar">
                          {hours.map(h => (
                            <div key={h.h} className="mt-bar-divider" style={{ left: `${h.pct}%` }} />
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}

              <div className="form-group">
                <label>Notes (optional)</label>
                <textarea
                  value={meetingForm.notes}
                  onChange={e => setMeetingForm(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Agenda, location, call link..."
                  rows={2}
                />
              </div>
            </div>
            <div className="meeting-form-footer">
              <button className="btn btn-secondary" onClick={() => setShowMeetingModal(false)}>Cancel</button>
              <button
                className={`btn ${hasHardConflict ? 'btn-danger' : 'btn-primary'}`}
                onClick={attemptSaveMeeting}
                disabled={!meetingForm.title || !meetingForm.date || hasHardConflict}
              >
                {hasHardConflict ? 'Protected Time Conflict' : editingMeetingId ? 'Update Meeting' : 'Create Meeting'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Protected Time Warning/Block Modal */}
      {protectedTimeWarning && (
        <div className="event-type-picker-overlay" onMouseDown={e => { if (e.target === e.currentTarget) setProtectedTimeWarning(null); }}>
          <div className="protected-time-modal">
            <div className={`pt-icon ${protectedTimeWarning.enforcement}`}>
              {protectedTimeWarning.enforcement === 'hard' ? '✕' : '⚠'}
            </div>
            <h3>{protectedTimeWarning.enforcement === 'hard' ? 'Protected Time — Cannot Schedule' : 'Protected Time Warning'}</h3>
            <p className="pt-message">
              This time overlaps with protected time for: <strong>{protectedTimeWarning.names.join(', ')}</strong>
            </p>
            {protectedTimeWarning.enforcement === 'hard' ? (
              <>
                <p className="pt-detail">Block enforcement is set to "hard" — scheduling during protected time is not allowed. Please pick a different time.</p>
                <div className="pt-actions">
                  <button className="btn btn-primary" onClick={() => setProtectedTimeWarning(null)}>Pick a Different Time</button>
                </div>
              </>
            ) : (
              <>
                <p className="pt-detail">There is no expectation that this meeting will be accepted during protected time. The attendee(s) may decline.</p>
                <div className="pt-actions">
                  <button className="btn btn-secondary" onClick={() => setProtectedTimeWarning(null)}>Pick a Different Time</button>
                  <button className="btn btn-warning" onClick={() => { setProtectedTimeWarning(null); saveMeeting(); }}>Schedule Anyway</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Event Type Picker (appears on calendar click) */}
      {showEventTypePicker && (
        <div className="event-type-picker-overlay" onMouseDown={e => { if (e.target === e.currentTarget) setShowEventTypePicker(null); }}>
          <div className="event-type-picker">
            <h3>What would you like to create?</h3>
            <div className="event-type-options">
              <button className="event-type-btn appointment" onClick={() => handleEventTypeSelect('appointment')}>
                <User size={20} />
                <span className="et-label">Appointment</span>
                <span className="et-desc">Client session</span>
              </button>
              <button className="event-type-btn meeting" onClick={() => handleEventTypeSelect('meeting')}>
                <Layers size={20} />
                <span className="et-label">Meeting</span>
                <span className="et-desc">Staff / internal</span>
              </button>
              <button className="event-type-btn personal" onClick={() => handleEventTypeSelect('personal')}>
                <Clock size={20} />
                <span className="et-label">Personal</span>
                <span className="et-desc">OOO, busy, focus</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Client Picker Modal */}
      {showClientPicker && (
        <div className="scheduling-assistant-overlay">
          <div className="client-picker-container">
            <button
              className="close-btn"
              onClick={() => setShowClientPicker(false)}
            >
              <X size={20} />
            </button>
            <h2 className="client-picker-title">Select Client</h2>
            <div className="client-picker-search">
              <Search size={16} />
              <input
                type="text"
                placeholder="Search clients..."
                value={clientPickerSearch}
                onChange={(e) => setClientPickerSearch(e.target.value)}
                autoFocus
              />
            </div>
            <div className="client-picker-list">
              {clientPickerLoading ? (
                <div className="client-picker-empty">Loading clients...</div>
              ) : clientPickerList
                  .filter(c => {
                    if (!clientPickerSearch) return true;
                    const term = clientPickerSearch.toLowerCase();
                    return c.first_name.toLowerCase().includes(term) ||
                      c.last_name.toLowerCase().includes(term) ||
                      c.email.toLowerCase().includes(term);
                  })
                  .length === 0 ? (
                <div className="client-picker-empty">No clients found</div>
              ) : clientPickerList
                  .filter(c => {
                    if (!clientPickerSearch) return true;
                    const term = clientPickerSearch.toLowerCase();
                    return c.first_name.toLowerCase().includes(term) ||
                      c.last_name.toLowerCase().includes(term) ||
                      c.email.toLowerCase().includes(term);
                  })
                  .map(client => (
                    <button
                      key={client.id}
                      className="client-picker-item"
                      onClick={() => handleClientSelected(client)}
                    >
                      <div className="client-picker-avatar">
                        {client.first_name?.[0]}{client.last_name?.[0]}
                      </div>
                      <div className="client-picker-info">
                        <span className="client-picker-name">{client.first_name} {client.last_name}</span>
                        <span className="client-picker-email">{client.email}</span>
                      </div>
                    </button>
                  ))
              }
            </div>
          </div>
        </div>
      )}

      {/* Scheduling Assistant Modal */}
      {showSchedulingAssistant && selectedClientId && selectedClientName && (
        <div className="scheduling-assistant-overlay">
          <div className="scheduling-assistant-container">
            <button
              className="close-btn"
              onClick={() => {
                setShowSchedulingAssistant(false);
                setSelectedDate(null);
                setSelectedClientId(null);
                setSelectedClientName(null);
                setTimelineEditAppointment(null);
                setTimelineEditScope(undefined);
              }}
            >
              <X size={20} />
            </button>
            <SchedulingAssistant
              clientId={selectedClientId}
              clientName={selectedClientName}
              clientTimezone={coachTimezone}
              initialDate={selectedDate || undefined}
              initialEditAppointment={timelineEditAppointment || undefined}
              initialEditScope={timelineEditScope}
              onAppointmentCreated={handleAppointmentCreated}
              onCancel={() => {
                setShowSchedulingAssistant(false);
                setSelectedDate(null);
                setSelectedClientId(null);
                setSelectedClientName(null);
                setTimelineEditAppointment(null);
                setTimelineEditScope(undefined);
              }}
            />
          </div>
        </div>
      )}

      {/* Appointment Edit Modal */}
      {editingAppointment && (
        <AppointmentEditModal
          appointment={editingAppointment}
          clientName={getClientName(editingAppointment)}
          clientTimezone={coachTimezone}
          isOpen={true}
          onClose={() => setEditingAppointment(null)}
          onUpdated={handleAppointmentUpdated}
          onDeleted={handleAppointmentDeleted}
          onEditInTimeline={
            editingAppointment.event_type === 'meeting' || editingAppointment.event_type === 'personal' || getClientId(editingAppointment)
              ? handleEditInTimeline
              : undefined
          }
        />
      )}

      <style>{styles}</style>
    </div>
  );
};

// ============================================================================
// STYLES
// ============================================================================

const styles = `
  .coach-calendar-view {
    display: flex;
    height: 100%;
    background: var(--bg-primary);
    overflow: hidden;
  }

  /* ========== SIDEBAR ========== */
  .calendar-sidebar {
    width: 240px;
    min-width: 240px;
    background: var(--bg-secondary);
    border-right: 1px solid var(--border-secondary);
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 16px;
    overflow-y: auto;
  }

  .new-event-dropdown-wrapper {
    position: relative;
  }
  .btn-new-appointment {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 12px 16px;
    background: var(--accent-green);
    color: white;
    border: none;
    border-radius: var(--radius-medium);
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.2s;
  }

  .btn-new-appointment:hover {
    background: #059669;
  }

  /* New Event Dropdown */
  .new-event-dropdown {
    position: absolute;
    top: calc(100% + 6px);
    left: 0;
    right: 0;
    background: var(--bg-primary);
    border: 1px solid var(--border-secondary);
    border-radius: var(--radius-medium);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
    z-index: 50;
    overflow: hidden;
    animation: dropdownSlide 0.12s ease-out;
  }
  @keyframes dropdownSlide {
    from { opacity: 0; transform: translateY(-4px); }
    to { opacity: 1; transform: translateY(0); }
  }
  .new-event-option {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 10px 14px;
    border: none;
    background: none;
    cursor: pointer;
    text-align: left;
    transition: background 0.15s;
    color: var(--text-primary);
  }
  .new-event-option:hover { background: var(--bg-tertiary); }
  .new-event-option .option-title { font-size: 13px; font-weight: 600; }
  .new-event-option .option-desc { font-size: 11px; color: var(--text-tertiary); }
  .new-event-option.appointment svg { color: #10b981; }
  .new-event-option.meeting svg { color: #3b82f6; }
  .new-event-option.personal svg { color: #f59e0b; }
  .new-event-option:not(:last-child) { border-bottom: 1px solid var(--border-secondary); }

  /* Event Type Picker (calendar click overlay) */
  .event-type-picker-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.4);
    z-index: 100;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .event-type-picker {
    background: var(--bg-primary);
    border: 1px solid var(--border-secondary);
    border-radius: var(--radius-large);
    padding: 20px;
    min-width: 340px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  }
  .event-type-picker h3 {
    margin: 0 0 16px;
    font-size: 16px;
    color: var(--text-primary);
    text-align: center;
  }
  .event-type-options {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .event-type-btn {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 16px;
    border: 1px solid var(--border-secondary);
    border-radius: var(--radius-medium);
    background: var(--bg-secondary);
    cursor: pointer;
    transition: all 0.15s;
    text-align: left;
    color: var(--text-primary);
  }
  .event-type-btn:hover { background: var(--bg-tertiary); border-color: var(--text-tertiary); }
  .event-type-btn .et-label { font-size: 14px; font-weight: 600; }
  .event-type-btn .et-desc { font-size: 12px; color: var(--text-tertiary); margin-left: auto; }
  /* Meeting Modal */
  .meeting-modal {
    background: var(--bg-primary);
    border: 1px solid var(--border-secondary);
    border-radius: var(--radius-large);
    padding: 24px;
    min-width: 750px;
    max-width: 950px;
    width: 90vw;
    max-height: 90vh;
    overflow-y: auto;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  }
  .meeting-modal h3 {
    margin: 0 0 16px;
    font-size: 16px;
    color: #3b82f6;
  }
  .meeting-form {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  .meeting-form .form-group {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .meeting-form label {
    font-size: 12px;
    font-weight: 600;
    color: var(--text-secondary);
  }
  .meeting-form input,
  .meeting-form select,
  .meeting-form textarea {
    padding: 8px 10px;
    border: 1px solid var(--border-secondary);
    border-radius: var(--radius-small);
    background: var(--bg-secondary);
    color: var(--text-primary);
    font-size: 13px;
    font-family: inherit;
  }
  .meeting-form textarea { resize: vertical; }
  .meeting-form .form-row-2 {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }
  .meeting-form .form-row-3 {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 12px;
  }
  .meeting-form-footer {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid var(--border-secondary);
  }

  /* Attendee Picker */
  .attendee-picker { position: relative; }
  .attendee-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-bottom: 6px;
  }
  .attendee-chip {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 8px;
    background: rgba(59, 130, 246, 0.15);
    border: 1px solid rgba(59, 130, 246, 0.3);
    border-radius: 12px;
    font-size: 12px;
    color: #3b82f6;
    font-weight: 500;
  }
  .attendee-chip button {
    background: none;
    border: none;
    cursor: pointer;
    color: #3b82f6;
    padding: 0;
    display: flex;
    opacity: 0.6;
  }
  .attendee-chip button:hover { opacity: 1; }

  .staff-autocomplete {
    position: absolute;
    left: 0;
    right: 0;
    top: 100%;
    background: var(--bg-primary);
    border: 1px solid var(--border-secondary);
    border-radius: var(--radius-medium);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
    z-index: 50;
    max-height: 200px;
    overflow-y: auto;
  }
  .autocomplete-option {
    display: flex;
    flex-direction: column;
    width: 100%;
    padding: 8px 12px;
    border: none;
    background: none;
    cursor: pointer;
    text-align: left;
    transition: background 0.15s;
    color: var(--text-primary);
  }
  .autocomplete-option:hover { background: var(--bg-tertiary); }
  .autocomplete-option:not(:last-child) { border-bottom: 1px solid var(--border-secondary); }
  .ac-name { font-size: 13px; font-weight: 500; }
  .ac-email { font-size: 11px; color: var(--text-tertiary); }
  .autocomplete-loading,
  .autocomplete-empty {
    padding: 12px;
    font-size: 12px;
    color: var(--text-tertiary);
    text-align: center;
  }

  /* Meeting Scheduling Timeline */
  .meeting-timeline {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .meeting-timeline label {
    font-size: 12px;
    font-weight: 600;
    color: var(--text-secondary);
  }
  .mt-timeline-container {
    border: 1px solid var(--border-secondary);
    border-radius: var(--radius-medium);
    overflow: visible;
    background: var(--bg-secondary);
  }
  .mt-hours {
    display: flex;
    border-bottom: 1px solid var(--border-secondary);
  }
  .mt-label-spacer {
    width: 140px;
    min-width: 140px;
    border-right: 1px solid var(--border-secondary);
  }
  .mt-hours-bar {
    flex: 1;
    position: relative;
    height: 20px;
  }
  .mt-hour-mark {
    position: absolute;
    font-size: 9px;
    color: var(--text-tertiary);
    transform: translateX(-50%);
    top: 4px;
  }
  .mt-participant-row {
    display: flex;
    border-bottom: 1px solid var(--border-secondary);
  }
  .mt-participant-row:last-child { border-bottom: none; }
  .mt-participant-label {
    width: 120px;
    min-width: 120px;
    padding: 4px 8px;
    font-size: 12px;
    font-weight: 500;
    color: var(--text-secondary);
    border-right: 1px solid var(--border-secondary);
    display: flex;
    align-items: center;
    gap: 4px;
    white-space: nowrap;
    overflow: hidden;
  }
  .mt-name {
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .mt-attendance-toggle {
    font-size: 9px;
    font-weight: 700;
    padding: 1px 4px;
    border-radius: 3px;
    border: 1px solid var(--border-secondary);
    cursor: pointer;
    flex-shrink: 0;
    transition: all 0.15s;
    text-transform: uppercase;
    letter-spacing: 0.3px;
  }
  .mt-attendance-toggle.required {
    background: rgba(59, 130, 246, 0.15);
    color: #3b82f6;
    border-color: rgba(59, 130, 246, 0.3);
  }
  .mt-attendance-toggle.optional {
    background: rgba(107, 114, 128, 0.15);
    color: #9ca3af;
    border-color: rgba(107, 114, 128, 0.3);
  }
  .mt-remove {
    background: none;
    border: none;
    cursor: pointer;
    color: var(--text-tertiary);
    padding: 2px;
    border-radius: 50%;
    display: flex;
    opacity: 0;
    transition: opacity 0.15s;
    margin-left: auto;
    flex-shrink: 0;
  }
  .mt-participant-row:hover .mt-remove { opacity: 1; }
  .mt-remove:hover { color: #ef4444; background: rgba(239, 68, 68, 0.1); }

  .mt-inline-search {
    position: relative;
    width: 100%;
  }
  .mt-search-input {
    width: 100%;
    border: none;
    background: transparent;
    color: var(--text-tertiary);
    font-size: 12px;
    padding: 2px 0;
    outline: none;
  }
  .mt-search-input:focus { color: var(--text-primary); }
  .mt-search-input::placeholder { color: var(--text-tertiary); }
  .mt-add-row { overflow: visible; position: relative; z-index: 10; }
  .mt-add-row .mt-participant-label { overflow: visible; }
  .mt-add-row .staff-autocomplete {
    position: absolute;
    left: -8px;
    width: 250px;
    top: 100%;
    margin-top: 2px;
    z-index: 100;
  }
  .mt-empty-bar { opacity: 0.3; }

  /* Conflict indicators */
  .mt-conflict-hard { color: #ef4444 !important; font-weight: 700; }
  .mt-conflict-soft { color: #f59e0b !important; font-weight: 700; }
  .mt-participant-row.conflict-hard { background: rgba(239, 68, 68, 0.05); }
  .mt-participant-row.conflict-soft { background: rgba(245, 158, 11, 0.05); }
  .mt-proposed-blocked {
    background: rgba(239, 68, 68, 0.15) !important;
    border-left-color: rgba(239, 68, 68, 0.7) !important;
    border-right-color: rgba(239, 68, 68, 0.7) !important;
  }
  .mt-proposed-warn {
    background: rgba(245, 158, 11, 0.15) !important;
    border-left-color: rgba(245, 158, 11, 0.7) !important;
    border-right-color: rgba(245, 158, 11, 0.7) !important;
  }
  .mt-participant-bar {
    flex: 1;
    position: relative;
    height: 32px;
    background: var(--bg-primary);
  }
  .mt-bar-divider {
    position: absolute;
    top: 0;
    bottom: 0;
    width: 1px;
    background: var(--border-secondary);
    opacity: 0.5;
  }
  .mt-busy-block {
    position: absolute;
    top: 4px;
    bottom: 4px;
    background: rgba(107, 114, 128, 0.4);
    border: 1px solid rgba(107, 114, 128, 0.6);
    border-radius: 2px;
    z-index: 2;
  }
  .mt-busy-block.mt-blocked {
    background: repeating-linear-gradient(
      -45deg,
      rgba(239, 68, 68, 0.25),
      rgba(239, 68, 68, 0.25) 3px,
      rgba(239, 68, 68, 0.1) 3px,
      rgba(239, 68, 68, 0.1) 6px
    );
    border: 1px solid rgba(239, 68, 68, 0.5);
  }
  .mt-proposed {
    position: absolute;
    top: 0;
    bottom: 0;
    background: rgba(59, 130, 246, 0.15);
    border-left: 2px solid rgba(59, 130, 246, 0.7);
    border-right: 2px solid rgba(59, 130, 246, 0.7);
    z-index: 3;
    cursor: grab;
    pointer-events: auto;
  }
  .mt-proposed.dragging { cursor: grabbing; }
  .mt-resize-handle {
    position: absolute;
    top: 0;
    bottom: 0;
    width: 6px;
    cursor: ew-resize;
    z-index: 4;
  }
  .mt-resize-left { left: -3px; }
  .mt-resize-right { right: -3px; }

  /* Protected Time Modal */
  .protected-time-modal {
    background: var(--bg-primary);
    border: 1px solid var(--border-secondary);
    border-radius: var(--radius-large);
    padding: 28px;
    max-width: 440px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
    text-align: center;
  }
  .pt-icon {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 24px;
    font-weight: 700;
    margin: 0 auto 16px;
  }
  .pt-icon.hard { background: rgba(239, 68, 68, 0.15); color: #ef4444; }
  .pt-icon.soft { background: rgba(245, 158, 11, 0.15); color: #f59e0b; }
  .protected-time-modal h3 {
    margin: 0 0 12px;
    font-size: 16px;
    color: var(--text-primary);
  }
  .pt-message {
    font-size: 14px;
    color: var(--text-secondary);
    margin: 0 0 8px;
  }
  .pt-detail {
    font-size: 12px;
    color: var(--text-tertiary);
    margin: 0 0 20px;
  }
  .pt-actions {
    display: flex;
    gap: 10px;
    justify-content: center;
  }
  .btn-warning {
    background: #f59e0b;
    color: white;
    padding: 8px 16px;
    border-radius: var(--radius-medium);
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    border: none;
  }
  .btn-warning:hover { opacity: 0.9; }

  .event-type-btn.appointment svg { color: #10b981; }
  .event-type-btn.meeting svg { color: #3b82f6; }
  .event-type-btn.personal svg { color: #f59e0b; }

  /* Personal Event Modal */
  .personal-event-modal {
    background: var(--bg-primary);
    border: 1px solid var(--border-secondary);
    border-radius: var(--radius-large);
    padding: 24px;
    min-width: 380px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  }
  .personal-event-modal h3 {
    margin: 0 0 16px;
    font-size: 16px;
    color: #f59e0b;
  }
  .personal-form {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  .personal-form .form-group {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .personal-form label {
    font-size: 12px;
    font-weight: 600;
    color: var(--text-secondary);
  }
  .personal-form input,
  .personal-form select {
    padding: 8px 10px;
    border: 1px solid var(--border-secondary);
    border-radius: var(--radius-small);
    background: var(--bg-secondary);
    color: var(--text-primary);
    font-size: 13px;
  }
  .personal-form .form-row-2 {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }
  .personal-allday-toggle {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: var(--text-secondary);
    cursor: pointer;
  }
  .personal-allday-toggle input { cursor: pointer; }
  .personal-form-footer {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid var(--border-secondary);
  }

  .timezone-info {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px;
    background: var(--bg-primary);
    border-radius: var(--radius-medium);
    font-size: 12px;
    color: var(--text-secondary);
  }

  .pending-bookings-widget {
    background: var(--bg-primary);
    border: 1px solid rgba(245, 158, 11, 0.3);
    border-radius: var(--radius-medium);
    overflow: hidden;
    margin-top: 12px;
  }
  .pending-header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 12px;
    font-size: 13px;
    font-weight: 600;
    color: #f59e0b;
    border-bottom: 1px solid rgba(245, 158, 11, 0.15);
  }
  .pending-badge {
    background: #f59e0b;
    color: white;
    font-size: 11px;
    font-weight: 700;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .pending-list {
    display: flex;
    flex-direction: column;
  }
  .pending-item {
    padding: 10px 12px;
    cursor: pointer;
    border-bottom: 1px solid var(--border-secondary);
    transition: background 0.15s;
  }
  .pending-item:last-child { border-bottom: none; }
  .pending-item:hover { background: rgba(245, 158, 11, 0.05); }
  .pending-name {
    font-size: 13px;
    font-weight: 600;
    color: var(--text-primary);
  }
  .pending-detail {
    font-size: 11px;
    color: var(--text-secondary);
    margin-top: 2px;
  }

  /* ========== OOO NAG WIDGET ========== */
  .ooo-nag-widget {
    background: var(--bg-primary);
    border: 1px solid rgba(124, 58, 237, 0.3);
    border-radius: var(--radius-medium);
    overflow: hidden;
    margin-top: 12px;
  }
  .ooo-nag-header {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 10px 12px;
    font-size: 13px;
    font-weight: 600;
    color: #7c3aed;
    border-bottom: 1px solid rgba(124, 58, 237, 0.15);
  }
  .ooo-nag-icon { font-size: 14px; }
  .ooo-nag-block {
    padding: 10px 12px;
    border-bottom: 1px solid var(--border-secondary);
  }
  .ooo-nag-block:last-child { border-bottom: none; }
  .ooo-nag-dates {
    font-size: 12px;
    font-weight: 600;
    color: #7c3aed;
  }
  .ooo-nag-count {
    font-size: 11px;
    color: #ef4444;
    font-weight: 500;
    margin-top: 4px;
  }
  .ooo-nag-appts {
    margin-top: 6px;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .ooo-nag-appt {
    display: flex;
    flex-direction: column;
    padding: 4px 8px;
    background: rgba(124, 58, 237, 0.05);
    border-radius: 4px;
  }
  .ooo-nag-appt-time {
    font-size: 10px;
    color: var(--text-tertiary);
  }
  .ooo-nag-appt-title {
    font-size: 11px;
    color: var(--text-primary);
    font-weight: 500;
  }
  .ooo-nag-more {
    font-size: 11px;
    color: var(--text-tertiary);
    font-style: italic;
    padding-left: 8px;
  }

  /* ========== PENDING MEETINGS NAG ========== */
  .pending-meetings-widget {
    background: var(--bg-primary);
    border: 1px solid rgba(59, 130, 246, 0.3);
    border-radius: var(--radius-medium);
    overflow: hidden;
    margin-top: 12px;
  }
  .pending-meetings-header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 12px;
    font-size: 13px;
    font-weight: 600;
    color: #3b82f6;
    border-bottom: 1px solid rgba(59, 130, 246, 0.15);
  }
  .pending-meetings-badge {
    background: #3b82f6;
    color: white;
    font-size: 11px;
    font-weight: 700;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .pending-meetings-list {
    display: flex;
    flex-direction: column;
  }
  .pending-meeting-item {
    padding: 10px 12px;
    cursor: pointer;
    border-bottom: 1px solid var(--border-secondary);
    transition: background 0.15s;
  }
  .pending-meeting-item:last-child { border-bottom: none; }
  .pending-meeting-item:hover { background: rgba(59, 130, 246, 0.05); }
  .pending-meeting-title {
    font-size: 13px;
    font-weight: 600;
    color: var(--text-primary);
  }
  .pending-meeting-detail {
    font-size: 11px;
    color: var(--text-secondary);
    margin-top: 2px;
  }

  /* ========== MINI CALENDAR ========== */
  .mini-calendar-sidebar {
    background: var(--bg-primary);
    border-radius: var(--radius-medium);
    padding: 12px;
  }

  .mini-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
  }

  .mini-nav-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    background: none;
    border: none;
    border-radius: 4px;
    color: var(--text-secondary);
    cursor: pointer;
    transition: all 0.15s;
  }

  .mini-nav-btn:hover {
    background: var(--bg-secondary);
    color: var(--text-primary);
  }

  .mini-month-label {
    background: none;
    border: none;
    font-size: 13px;
    font-weight: 600;
    color: var(--text-primary);
    cursor: pointer;
    padding: 4px 8px;
    border-radius: 4px;
    transition: background 0.15s;
  }

  .mini-month-label:hover {
    background: var(--bg-secondary);
  }

  .mini-grid {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 2px;
  }

  .mini-weekday {
    text-align: center;
    font-size: 10px;
    font-weight: 500;
    color: var(--text-secondary);
    padding: 4px 0;
  }

  .mini-day {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    font-size: 11px;
    color: var(--text-primary);
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.15s;
  }

  .mini-day:hover {
    background: var(--bg-secondary);
  }

  .mini-day.other-month {
    color: var(--text-tertiary);
    opacity: 0.5;
  }

  .mini-day.today {
    background: var(--accent-green);
    color: white;
    font-weight: 600;
  }

  .mini-day.selected {
    background: var(--accent-blue);
    color: white;
  }

  .mini-day.in-week {
    background: rgba(59, 130, 246, 0.1);
  }

  .mini-day.in-week.today,
  .mini-day.in-week.selected {
    background: var(--accent-blue);
  }

  /* ========== MAIN AREA ========== */
  .calendar-main {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .calendar-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 20px;
    border-bottom: 1px solid var(--border-secondary);
    background: var(--bg-secondary);
  }

  .header-left {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  .nav-buttons {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .nav-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 6px 10px;
    background: transparent;
    border: 1px solid var(--border);
    border-radius: var(--radius-small);
    color: var(--text-secondary);
    font-size: 13px;
    cursor: pointer;
    transition: all 0.15s;
  }

  .nav-btn:hover {
    background: var(--bg-primary);
    border-color: var(--accent-green);
    color: var(--accent-green);
  }

  .today-btn {
    padding: 6px 14px;
  }

  .calendar-title {
    font-size: 18px;
    font-weight: 600;
    color: var(--text-primary);
    margin: 0;
  }

  .header-right {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .view-toggle {
    display: flex;
    background: var(--bg-primary);
    border-radius: var(--radius-medium);
    padding: 2px;
    border: 1px solid var(--border-secondary);
  }

  .toggle-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    background: transparent;
    border: none;
    border-radius: var(--radius-small);
    color: var(--text-secondary);
    font-size: 12px;
    cursor: pointer;
    transition: all 0.15s;
  }

  .toggle-btn:hover {
    color: var(--text-primary);
  }

  .toggle-btn.active {
    background: var(--accent-green);
    color: white;
  }

  .calendar-content {
    flex: 1;
    overflow: hidden;
    position: relative;
  }

  /* ========== LOADING ========== */
  .loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(var(--bg-secondary-rgb), 0.8);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;
  }

  .loading-spinner {
    width: 32px;
    height: 32px;
    border: 3px solid var(--border-secondary);
    border-top-color: var(--accent-green);
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  .spinning {
    animation: spin 1s linear infinite;
  }

  /* ========== MONTH VIEW ========== */
  .month-view {
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  .day-headers {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    background: var(--bg-secondary);
    border-bottom: 1px solid var(--border-secondary);
  }

  .day-header {
    padding: 10px;
    text-align: center;
    font-size: 12px;
    font-weight: 600;
    color: var(--text-secondary);
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .day-cells {
    flex: 1;
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    grid-auto-rows: 1fr;
    overflow: hidden;
  }

  .day-cell {
    border-right: 1px solid var(--border-secondary);
    border-bottom: 1px solid var(--border-secondary);
    padding: 4px 6px;
    min-height: 100px;
    cursor: pointer;
    transition: background 0.15s;
    overflow: hidden;
    background: var(--bg-primary);
    display: flex;
    flex-direction: column;
  }

  .day-cell:nth-child(7n) {
    border-right: none;
  }

  .day-cell:hover {
    background: var(--bg-secondary);
  }

  .day-cell.other-month {
    background: var(--bg-secondary);
    opacity: 0.5;
  }

  .day-cell.today {
    background: rgba(16, 185, 129, 0.05);
  }

  .day-cell.today .day-number {
    background: var(--accent-green);
    color: white;
    border-radius: 50%;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .day-number {
    font-size: 13px;
    font-weight: 500;
    color: var(--text-primary);
    margin-bottom: 4px;
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .ext-dot {
    width: 10px;
    height: 6px;
    border-radius: 50%;
    background: #ef4444;
    flex-shrink: 0;
  }

  .day-appointments {
    display: flex;
    flex-direction: column;
    gap: 2px;
    flex: 1;
  }

  .appointment-chip {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 2px 6px;
    background: color-mix(in srgb, var(--apt-color, #10b981) 45%, var(--bg-primary));
    border-left: 2px solid var(--apt-color, #10b981);
    border-radius: 2px;
    font-size: 11px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    cursor: pointer;
    transition: background 0.15s, transform 0.1s;
  }

  .appointment-chip:hover {
    background: color-mix(in srgb, var(--apt-color, #10b981) 55%, var(--bg-primary));
    transform: translateX(2px);
  }

  .appointment-chip.pending_approval {
    background: color-mix(in srgb, var(--apt-color, #10b981) 35%, var(--bg-primary));
    border-left-color: var(--apt-color, #10b981);
    border-left-style: dashed;
  }

  .appointment-chip.completed {
    background: rgba(107, 114, 128, 0.1);
    border-left-color: #6b7280;
  }

  .appointment-chip.cancelled {
    background: rgba(239, 68, 68, 0.1);
    border-left-color: #ef4444;
    text-decoration: line-through;
    opacity: 0.6;
  }

  .appointment-chip.external-busy {
    background: rgba(239, 68, 68, 0.1);
    border-left-color: #ef4444;
  }
  .appointment-chip.external-busy .apt-client {
    color: #ef4444;
    font-weight: 600;
    font-size: 10px;
    text-transform: uppercase;
  }

  .appointment-chip .apt-time {
    font-weight: 500;
    color: var(--text-secondary);
  }

  .appointment-chip .apt-client {
    color: var(--text-primary);
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .more-appointments {
    font-size: 12px;
    color: var(--text-secondary);
    padding: 2px 6px;
    margin-top: auto;
    text-align: right;
  }

  /* ========== WEEK VIEW ========== */
  .week-view {
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .week-header {
    display: flex;
    border-bottom: 1px solid var(--border-secondary);
    background: var(--bg-secondary);
  }

  .time-gutter-header {
    width: 60px;
    min-width: 60px;
    border-right: 1px solid var(--border-secondary);
  }

  .week-day-header {
    flex: 1;
    display: flex;
    flex-direction: row;
    align-items: baseline;
    gap: 6px;
    padding: 10px 12px;
    border-right: 1px solid var(--border-secondary);
  }

  .week-day-header:last-child {
    border-right: none;
  }

  .week-day-header.today {
    background: rgba(16, 185, 129, 0.05);
  }

  .week-day-header .day-date {
    font-size: 18px;
    font-weight: 700;
    color: var(--text-primary);
  }

  .week-day-header .day-name {
    font-size: 14px;
    font-weight: 500;
    color: var(--text-secondary);
  }

  .week-day-header.today .day-date {
    background: var(--accent-green);
    color: white;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
  }


  /* All-day events banner */
  .allday-banner {
    display: flex;
    border-bottom: 2px solid var(--border-secondary);
    background: var(--bg-secondary);
    min-height: 28px;
  }
  .allday-gutter-label {
    width: 60px;
    min-width: 60px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    color: var(--text-tertiary);
    text-transform: uppercase;
    letter-spacing: 0.5px;
    border-right: 1px solid var(--border-secondary);
  }
  .allday-grid {
    flex: 1;
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 0 4px;
    padding: 3px 4px;
    align-items: center;
  }
  .allday-chip {
    padding: 3px 8px;
    border-radius: 3px;
    font-size: 11px;
    font-weight: 600;
    cursor: pointer;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    text-align: center;
    border-left: 4px solid var(--apt-border-color, #7c3aed);
    background: color-mix(in srgb, var(--apt-color, #3b82f6) 45%, var(--bg-primary));
    color: var(--text-primary);
    transition: background 0.15s;
  }
  .allday-chip:hover {
    background: color-mix(in srgb, var(--apt-color, #3b82f6) 55%, var(--bg-primary));
  }

  .week-grid {
    flex: 1;
    display: flex;
    overflow-y: auto;
  }

  .time-gutter {
    width: 60px;
    min-width: 60px;
    border-right: 1px solid var(--border-secondary);
    background: var(--bg-secondary);
    min-height: 1920px;
  }

  .time-label {
    height: 40px;
    display: flex;
    align-items: flex-start;
    justify-content: flex-end;
    padding: 0 8px;
    font-size: 11px;
    font-weight: 600;
    color: var(--text-secondary);
    transform: translateY(-7px);
  }
  .time-label.half-hour {
    font-size: 10px;
    font-weight: 400;
    color: var(--text-tertiary);
  }

  .day-column {
    flex: 1;
    position: relative;
    border-right: 1px solid var(--border-secondary);
    background: var(--bg-primary);
    min-height: 1920px;
  }

  .day-column:last-child {
    border-right: none;
  }

  .day-column.today {
    background: rgba(16, 185, 129, 0.02);
  }

  .time-slot {
    height: 40px;
    border-bottom: 1px solid color-mix(in srgb, var(--border-secondary) 50%, transparent);
    cursor: pointer;
    transition: background 0.15s;
    position: relative;
  }
  .time-slot.on-hour {
    border-bottom: 1px solid var(--border-secondary);
  }
  /* 15-minute sub-line */
  .time-slot::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    top: 50%;
    height: 1px;
    background: color-mix(in srgb, var(--border-secondary) 25%, transparent);
    pointer-events: none;
  }

  .time-slot:hover {
    background: rgba(16, 185, 129, 0.05);
  }

  .availability-overlay {
    background: repeating-linear-gradient(
      -45deg,
      transparent,
      transparent 4px,
      rgba(16, 185, 129, 0.15) 4px,
      rgba(16, 185, 129, 0.15) 8px
    );
    border: none;
    border-radius: 4px;
    pointer-events: auto;
    cursor: default;
    overflow: hidden;
    transition: opacity 0.15s;
  }
  .availability-overlay:hover {
    opacity: 0.8;
  }

  .blocked-time-overlay {
    background: repeating-linear-gradient(
      -45deg,
      transparent,
      transparent 4px,
      rgba(239, 68, 68, 0.12) 4px,
      rgba(239, 68, 68, 0.12) 8px
    );
    border: none;
    border-radius: 4px;
    pointer-events: auto;
    cursor: default;
    overflow: hidden;
    transition: opacity 0.15s;
  }
  .blocked-time-overlay:hover {
    opacity: 0.8;
  }

  .current-time-marker {
    position: absolute;
    left: 0;
    right: 0;
    z-index: 8;
    pointer-events: none;
    display: flex;
    align-items: center;
  }
  .time-marker-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: #ef4444;
    margin-left: -5px;
    flex-shrink: 0;
  }
  .time-marker-line {
    flex: 1;
    height: 2px;
    border-top: 2px dashed #ef4444;
  }

  .avail-label {
    display: none;
  }

  .external-event-overlay {
    background: rgba(239, 68, 68, 0.08);
    border-left: 3px solid rgba(239, 68, 68, 0.5);
    border-radius: 4px;
    pointer-events: auto;
    cursor: default;
    display: flex;
    align-items: flex-start;
    padding: 2px 6px;
    transition: background 0.15s;
  }
  .external-event-overlay:hover {
    background: rgba(239, 68, 68, 0.15);
  }
  .ext-label {
    font-size: 10px;
    font-weight: 600;
    color: rgba(239, 68, 68, 0.7);
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  .ext-provider-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 14px;
    height: 14px;
    padding: 0 3px;
    margin-left: auto;
    border-radius: 3px;
    font-size: 9px;
    font-weight: 700;
    line-height: 1;
    flex-shrink: 0;
  }

  .appointments-container {
    position: absolute;
    top: 0;
    left: 4px;
    right: 4px;
    pointer-events: none;
    z-index: 5;
  }

  .week-appointment {
    position: absolute;
    background: color-mix(in srgb, var(--bg-primary) 80%, var(--apt-color, #10b981));
    border-left: 3px solid var(--apt-color, #10b981);
    border-radius: 4px;
    padding: 4px 6px;
    overflow: hidden;
    pointer-events: auto;
    cursor: pointer;
    transition: box-shadow 0.15s;
    display: flex;
    flex-direction: column;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
    outline: none;
  }
  .week-appointment:focus,
  .week-appointment:active {
    outline: none;
  }

  .week-appointment:hover {
    box-shadow: 0 2px 8px color-mix(in srgb, var(--apt-color, #10b981) 30%, transparent);
  }
  .week-appointment.cancelled:hover {
    box-shadow: 0 2px 8px rgba(239, 68, 68, 0.3);
  }
  .week-appointment.completed:hover {
    box-shadow: 0 2px 8px rgba(107, 114, 128, 0.3);
  }

  .week-appointment.dragging {
    cursor: grabbing;
    pointer-events: none;
  }

  /* Prevent text selection during drag */
  .week-view.dragging {
    user-select: none;
    cursor: grabbing;
  }

  .week-view.dragging .time-slot {
    pointer-events: none;
  }

  .week-view.dragging .week-appointment:not(.dragging) {
    opacity: 0.5;
  }

  .week-appointment.pending_approval {
    background: color-mix(in srgb, var(--bg-primary) 60%, var(--apt-color, #10b981));
    border: 1px solid color-mix(in srgb, var(--apt-color, #10b981) 30%, transparent);
    border-left: 3px dashed var(--apt-color, #10b981);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  }

  .week-appointment.completed {
    background: rgba(107, 114, 128, 0.15);
    border-left-color: #6b7280;
  }

  .week-appointment.cancelled {
    background: rgba(239, 68, 68, 0.15);
    border-left-color: #ef4444;
    opacity: 0.6;
  }

  .week-appointment .apt-content {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .week-appointment .apt-time {
    font-size: 10px;
    font-weight: 500;
    color: var(--text-secondary);
  }

  .week-appointment .apt-title {
    font-size: 11px;
    font-weight: 500;
    color: var(--text-primary);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .week-appointment .apt-client {
    font-size: 10px;
    color: var(--text-secondary);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* Resize handles */
  .week-appointment .resize-handle {
    position: absolute;
    left: 0;
    right: 0;
    height: 6px;
    cursor: ns-resize;
    opacity: 0;
    transition: opacity 0.15s;
    z-index: 10;
  }

  .week-appointment:hover .resize-handle {
    opacity: 1;
  }

  .week-appointment .resize-handle.top {
    top: 0;
    background: linear-gradient(to bottom, var(--apt-color, #10b981) 0%, transparent 100%);
    border-radius: 4px 4px 0 0;
  }

  .week-appointment .resize-handle.bottom {
    bottom: 0;
    background: linear-gradient(to top, var(--apt-color, #10b981) 0%, transparent 100%);
    border-radius: 0 0 4px 4px;
  }

  .week-appointment.cancelled .resize-handle.top {
    background: linear-gradient(to bottom, #ef4444 0%, transparent 100%);
  }
  .week-appointment.cancelled .resize-handle.bottom {
    background: linear-gradient(to top, #ef4444 0%, transparent 100%);
  }
  .week-appointment.completed .resize-handle.top {
    background: linear-gradient(to bottom, #6b7280 0%, transparent 100%);
  }
  .week-appointment.completed .resize-handle.bottom {
    background: linear-gradient(to top, #6b7280 0%, transparent 100%);
  }

  .week-appointment.resizing .resize-handle {
    opacity: 1;
  }

  /* ===== Outlook-style left border status indicator ===== */
  /* All events get a 6px left border — the status bar */
  .week-appointment {
    border-left-width: 10px;
  }

  /* Free status — outlined bar (hollow interior) */
  .week-appointment.free-status {
    border-left: none;
    padding-left: 18px;
  }
  .week-appointment.free-status::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 8px;
    border: 2px solid var(--apt-border-color, #333);
    border-radius: 2px;
    z-index: 1;
  }

  /* Tentative status — striped bar */
  .week-appointment.tentative-stripes {
    border-left: none;
    padding-left: 16px;
  }
  .week-appointment.tentative-stripes::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 10px;
    background: repeating-linear-gradient(
      -45deg,
      var(--apt-border-color, #333) 0px,
      var(--apt-border-color, #333) 2px,
      transparent 2px,
      transparent 5px
    );
    z-index: 1;
  }

  /* Declined meetings — very muted */
  .week-appointment.declined-status {
    opacity: 0.35;
    border-left-style: dashed;
    text-decoration: line-through;
  }

  /* Resize mode styles */
  .week-view.resizing {
    user-select: none;
    cursor: ns-resize;
  }

  .week-view.resizing .time-slot {
    pointer-events: none;
  }

  .week-view.resizing .week-appointment:not(.resizing) {
    opacity: 0.5;
  }

  /* ========== DAY VIEW ========== */
  .day-view {
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .day-view-header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px 20px;
    background: var(--bg-secondary);
    border-bottom: 1px solid var(--border-secondary);
  }

  .day-view-header.today {
    background: rgba(16, 185, 129, 0.05);
  }

  .day-view-header h3 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: var(--text-primary);
  }

  .today-badge {
    padding: 2px 8px;
    background: var(--accent-green);
    color: white;
    font-size: 11px;
    font-weight: 500;
    border-radius: 10px;
  }

  /* Day view all-day banner */
  .day-allday-banner {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 6px 12px;
    background: var(--bg-secondary);
    border-bottom: 2px solid var(--border-secondary);
  }
  .day-allday-label {
    font-size: 10px;
    color: var(--text-tertiary);
    text-transform: uppercase;
    letter-spacing: 0.5px;
    min-width: 50px;
  }
  .day-allday-events {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    flex: 1;
  }

  .day-grid {
    flex: 1;
    display: flex;
    overflow-y: auto;
  }

  .day-column.single {
    min-width: 200px;
  }

  .day-column.single .appointments-container {
    right: 4px;
    left: 4px;
  }

  /* Day view uses taller time slots (120px/hr vs 80px/hr) */
  .day-view .time-label {
    height: 60px;
  }
  .day-view .time-slot {
    height: 60px;
  }
  .day-view .time-gutter {
    min-height: 2880px;
  }
  .day-view .day-column {
    min-height: 2880px;
  }

  .day-appointment {
    position: absolute;
    background: color-mix(in srgb, var(--bg-primary) 80%, var(--apt-color, #10b981));
    border-left: 3px solid var(--apt-color, #10b981);
    border-radius: 4px;
    padding: 6px 10px;
    overflow: hidden;
    pointer-events: auto;
    cursor: pointer;
    transition: box-shadow 0.15s;
    display: flex;
    flex-direction: column;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
    outline: none;
  }

  .day-appointment:hover {
    box-shadow: 0 2px 8px color-mix(in srgb, var(--apt-color, #10b981) 30%, transparent);
  }

  .day-appointment.pending_approval {
    background: color-mix(in srgb, var(--bg-primary) 60%, var(--apt-color, #10b981));
    border: 1px solid color-mix(in srgb, var(--apt-color, #10b981) 30%, transparent);
    border-left: 3px dashed var(--apt-color, #10b981);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  }
  .day-appointment.pending_approval:hover {
    box-shadow: 0 2px 8px color-mix(in srgb, var(--apt-color, #10b981) 40%, transparent);
  }

  .day-appointment.completed {
    background: rgba(107, 114, 128, 0.15);
    border-left-color: #6b7280;
  }
  .day-appointment.completed:hover {
    box-shadow: 0 2px 8px rgba(107, 114, 128, 0.3);
  }

  .day-appointment.cancelled {
    background: rgba(239, 68, 68, 0.15);
    border-left-color: #ef4444;
    opacity: 0.6;
  }
  .day-appointment.cancelled:hover {
    box-shadow: 0 2px 8px rgba(239, 68, 68, 0.3);
  }

  .day-appointment .apt-content {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .day-appointment .apt-time {
    font-size: 12px;
    font-weight: 500;
    color: var(--text-secondary);
  }

  .day-appointment .apt-title {
    font-size: 14px;
    font-weight: 600;
    color: var(--text-primary);
  }

  .day-appointment .apt-client {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    color: var(--text-secondary);
  }

  .day-appointment .apt-notes {
    font-size: 11px;
    color: var(--text-tertiary);
    margin-top: 4px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .day-appointment .resize-handle {
    position: absolute;
    left: 0;
    right: 0;
    height: 6px;
    cursor: ns-resize;
    opacity: 0;
    transition: opacity 0.15s;
    z-index: 10;
  }
  .day-appointment:hover .resize-handle { opacity: 1; }
  .day-appointment .resize-handle.top {
    top: 0;
    background: linear-gradient(to bottom, var(--apt-color, #10b981) 0%, transparent 100%);
    border-radius: 4px 4px 0 0;
  }
  .day-appointment .resize-handle.bottom {
    bottom: 0;
    background: linear-gradient(to top, var(--apt-color, #10b981) 0%, transparent 100%);
    border-radius: 0 0 4px 4px;
  }
  .day-appointment.cancelled .resize-handle.top {
    background: linear-gradient(to bottom, #ef4444 0%, transparent 100%);
  }
  .day-appointment.cancelled .resize-handle.bottom {
    background: linear-gradient(to top, #ef4444 0%, transparent 100%);
  }
  .day-appointment.completed .resize-handle.top {
    background: linear-gradient(to bottom, #6b7280 0%, transparent 100%);
  }
  .day-appointment.completed .resize-handle.bottom {
    background: linear-gradient(to top, #6b7280 0%, transparent 100%);
  }

  /* Day view — same Outlook-style border treatment */
  .day-appointment {
    border-left-width: 10px;
  }
  .day-appointment.free-status {
    border-left: none;
    padding-left: 20px;
  }
  .day-appointment.free-status::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 8px;
    border: 2px solid var(--apt-border-color, #333);
    border-radius: 2px;
    z-index: 1;
  }
  .day-appointment.tentative-stripes {
    border-left: none;
    padding-left: 20px;
  }
  .day-appointment.tentative-stripes::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 10px;
    background: repeating-linear-gradient(
      -45deg,
      var(--apt-border-color, #333) 0px,
      var(--apt-border-color, #333) 2px,
      transparent 2px,
      transparent 5px
    );
    z-index: 1;
  }

  .day-appointment.declined-status {
    opacity: 0.35;
    border-left-style: dashed;
    text-decoration: line-through;
  }

  .day-view.dragging { user-select: none; cursor: grabbing; }
  .day-view.dragging .time-slot { pointer-events: none; }
  .day-view.dragging .day-appointment:not(.dragging) { opacity: 0.5; }
  .day-view.resizing { user-select: none; }
  .day-view.resizing .time-slot { pointer-events: none; }
  .day-view.resizing .day-appointment:not(.resizing) { opacity: 0.5; }

  /* ========== MODALS ========== */
  .scheduling-assistant-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 24px;
  }

  .scheduling-assistant-container {
    position: relative;
    width: 100%;
    max-width: 900px;
    max-height: 90vh;
    background: var(--bg-secondary);
    border-radius: var(--radius-large);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
    overflow: hidden;
  }

  .close-btn {
    position: absolute;
    top: 12px;
    right: 12px;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--bg-primary);
    border: 1px solid var(--border);
    border-radius: 50%;
    color: var(--text-secondary);
    cursor: pointer;
    z-index: 10;
    transition: all 0.15s;
  }

  .close-btn:hover {
    background: var(--bg-secondary);
    color: var(--text-primary);
    border-color: var(--accent-green);
  }

  /* ========== CLIENT PICKER ========== */
  .client-picker-container {
    position: relative;
    width: 100%;
    max-width: 420px;
    max-height: 80vh;
    background: var(--bg-secondary);
    border-radius: var(--radius-large);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
    padding: 24px;
    display: flex;
    flex-direction: column;
  }

  .client-picker-title {
    font-size: 18px;
    font-weight: 600;
    color: var(--text-primary);
    margin: 0 0 16px;
  }

  .client-picker-search {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 12px;
    background: var(--bg-primary);
    border: 1px solid var(--border-secondary);
    border-radius: 8px;
    margin-bottom: 12px;
  }

  .client-picker-search svg {
    color: var(--text-tertiary);
    flex-shrink: 0;
  }

  .client-picker-search input {
    flex: 1;
    background: none;
    border: none;
    outline: none;
    font-size: 14px;
    color: var(--text-primary);
  }

  .client-picker-search input::placeholder {
    color: var(--text-tertiary);
  }

  .client-picker-list {
    overflow-y: auto;
    max-height: 400px;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .client-picker-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 12px;
    background: none;
    border: 1px solid transparent;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.15s ease;
    text-align: left;
    width: 100%;
  }

  .client-picker-item:hover {
    background: var(--bg-primary);
    border-color: var(--accent-green);
  }

  .client-picker-avatar {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: linear-gradient(135deg, var(--accent-green), #059669);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    font-weight: 700;
    flex-shrink: 0;
  }

  .client-picker-info {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  .client-picker-name {
    font-size: 14px;
    font-weight: 500;
    color: var(--text-primary);
  }

  .client-picker-email {
    font-size: 12px;
    color: var(--text-secondary);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .client-picker-empty {
    padding: 24px;
    text-align: center;
    color: var(--text-secondary);
    font-size: 14px;
  }

  /* ========== RESPONSIVE ========== */
  @media (max-width: 900px) {
    .coach-calendar-view {
      flex-direction: column;
    }

    .calendar-sidebar {
      width: 100%;
      min-width: unset;
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      border-right: none;
      border-bottom: 1px solid var(--border-secondary);
      padding: 12px;
    }

    .mini-calendar-sidebar {
      display: none;
    }

    .timezone-info {
      padding: 8px;
    }

    .view-toggle {
      flex-wrap: wrap;
    }

    .toggle-btn {
      padding: 4px 8px;
      font-size: 11px;
    }

    .toggle-btn span {
      display: none;
    }
  }
`;

export default CoachCalendarView;
